import type { GrowthSessionReport, HubSpotFunnelAnalytics } from '@/types/hubspotFunnel'
import type { AccountFunnelAnalytics } from '@/types/accountFunnel'
import axios from 'axios'
import { authTokenManager } from '@/utils/authTokenManager'
import type { ProductEngagementEventName, SchemaCoverage } from 'aeo-schema-generator-shared/utils'
import type { FirstWeekResponse } from 'aeo-schema-generator-shared/types'
import type { ChiefAccessState, ChiefBetaParticipant, ChiefBriefing, ChiefChatHistoryMessage, ChiefChatResponse, ChiefNudge, ChiefRun, ChiefTimelineEvent, RecoReport } from '@/types/chief'
import type { DeploymentHealthOverview } from '@/types/deploymentHealth'
import type {
  OnboardingFixPreviewResponse,
  OnboardingFixProfileEdits,
  OnboardingFixProfileResponse,
  OnboardingFixTargetResponse,
} from '@/types/onboardingFixFlow'
import type { FreePageByAddressAnswer, FreePageForScanAnswer, OnboardingStanding, PageFactsSnapshot, PutLiveJob } from '@/types/hubspotOnboarding'
import type {
  User,
  CreditTransaction,
  SchemaGenerationResult,
  PaginatedResponse,
  ApiResponse,
  JsonLdSchema,
  UserDomain,
  DiscoveredUrl,
  UrlLibraryFilters,
  SupportTicket,
  CreateSupportTicketRequest,
  ReleaseNote,
  CreateReleaseNoteRequest,
  UpdateReleaseNoteRequest,
  TeamMember,
  TeamInvite,
  TeamInviteDetails,
  TeamInviteValidation,
  CurrentTeamResponse,
  ListTeamsResponse,
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationCompleteness,
  GetOrganizationResponse,
  RoadmapCategory,
  RoadmapFeature,
  RoadmapFeatureWithVoters,
  RoadmapFeaturesResponse,
  CreateRoadmapIdeaRequest,
  CreateRoadmapFeatureRequest,
  UpdateRoadmapFeatureRequest,
  ReorderRoadmapFeaturesRequest,
  CreateRoadmapCategoryRequest,
  UpdateRoadmapCategoryRequest,
  AdminRoadmapStats,
  SubscriptionPlan,
  UserSubscription,
  BillingOverview,
  CreateSubscriptionCheckoutResponse,
  PaymentMethodSetupResponse,
  UserBillingInfo,
  UpdateBillingInfoRequest,
  Invoice,
  GrantedProAccess,
  GrantHistoryResponse,
  UnifiedDomain,
  OnboardingIntentProfile,
  BlogPost,
  BlogPostsResponse,
  BlogCategoryCount,
  CreateBlogPostRequest,
  UpdateBlogPostRequest,
  AdminTutorialVideo,
  TutorialVideoAnalytics,
  TutorialVideoEventType,
  TutorialVideoPlacement,
  TutorialVideoSignedUpload,
  EducationAdminOverview,
  LearnListing,
  LearnVideoDetail,
  LearnCategoryDetail,
  YouTubeLookupResult,
  EducationCategory,
  EducationItem,
  EducationSeries,
  EducationTag,
  DemoRequest,
  CreateDemoRequestRequest,
  UpdateDemoRequestRequest,
  AgencyApplication,
  CreateAgencyApplicationRequest,
  UpdateAgencyApplicationRequest,
  ManagedApplication,
  CreateManagedApplicationRequest,
  UpdateManagedApplicationRequest
} from '@shared/types'

// In production, API is served from same origin as the client (supports both superschema.ai and www.superschema.ai)
// In development, use VITE_API_URL from .env or default to localhost:3001
// Note: VITE_API_URL is intentionally not set in production to allow dynamic origin resolution
// `typeof window` guard: the public header pulls this module into Astro's
// build-time render of every static page, where there is no window. The
// origin is only ever used in a browser, so an empty value there is never read.
const API_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.MODE === 'production'
    ? (typeof window !== 'undefined' ? window.location.origin : '')
    : 'http://localhost:3001')

const PUBLIC_ROUTE_PREFIXES = [
  '/resources',
  '/scan',
  '/onboarding/claim',
  '/team/join',
  '/sign-in',
  '/sign-up',
  '/ai/',
  '/aeo',
  '/geo',
  '/ai-search-optimization',
  '/schema-markup',
  '/registry',
  '/docs',
  // The page that waits on a read under an account. It asks the server every
  // three seconds, so it is the page most likely to have a question refused
  // for want of a token, and it handles that itself: a refused question is a
  // failed try, asked again, and a session that has really ended goes to
  // sign in with the way back. A prefix, because the path carries the read's id.
  '/welcome/reading',
]

const PUBLIC_ROUTE_EXACT = new Set([
  '/',
  '/pricing',
  '/hubspot-aeo',
  '/audit',
  '/ai-clarity-audit',
  '/ai-clarity-report',
  '/sign-in',
  '/sign-up',
  '/welcome/intent',
  '/welcome/results',
  '/thank-you',
  '/terms',
  '/privacy',
  '/docs',
  '/contact',
  '/methodology',
  '/free-schema-generator',
  '/faq-schema-generator',
  '/article-schema-generator',
  '/blogposting-schema-generator',
  '/howto-schema-generator',
  '/organization-schema-generator',
  '/localbusiness-schema-generator',
  '/product-schema-generator',
  '/recipe-schema-generator',
  '/event-schema-generator',
  '/video-schema-generator',
  '/review-schema-generator',
  '/breadcrumb-schema-generator',
  '/service-schema-generator',
  '/schema-markup-grader',
  '/aeo',
  '/geo',
  '/ai-search-optimization',
  '/schema-markup',
  '/schema-markup/improve-quality-score',
])

export function isPublicRoute(pathname: string): boolean {
  const normalizedPath = pathname.split('?')[0].replace(/\/+$/, '') || '/'

  if (PUBLIC_ROUTE_EXACT.has(normalizedPath)) {
    return true
  }

  return PUBLIC_ROUTE_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))
}

export function shouldRedirectToSignInOnUnauthorized(pathname: string, isUserSignedIn: boolean): boolean {
  if (isUserSignedIn || isPublicRoute(pathname)) {
    return false
  }

  return !pathname.includes('/sign-in')
}

// Create axios instance
export const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 120000, // 120 seconds (2 minutes) for schema generation
})

// Configure request interceptor at module load time
// This ensures the interceptor is ALWAYS active before any API calls are made
// The authTokenManager reference is updated by ApiProvider when auth state changes
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await authTokenManager.getToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
        console.log('✅ [API] Auth token attached:', {
          url: config.url,
          method: config.method?.toUpperCase(),
          hasToken: true,
          tokenPreview: token.substring(0, 20) + '...',
          authStatus: authTokenManager.getStatus()
        })
      } else {
        console.warn('⚠️ [API] No auth token available:', {
          url: config.url,
          method: config.method?.toUpperCase(),
          authStatus: authTokenManager.getStatus()
        })
      }
    } catch (error) {
      console.error('❌ [API] Failed to get auth token:', {
        url: config.url,
        method: config.method?.toUpperCase(),
        error: error instanceof Error ? error.message : 'Unknown error',
        authStatus: authTokenManager.getStatus()
      })
    }
    return config
  },
  (error) => {
    console.error('❌ [API] Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Configure response interceptor for error handling with 401 retry logic
api.interceptors.response.use(
  (response) => {
    console.log('✅ [API] Response received:', {
      url: response.config.url,
      status: response.status,
      statusText: response.statusText
    })
    return response
  },
  async (error) => {
    const status = error.response?.status
    const url = error.config?.url
    const originalRequest = error.config

    console.error('❌ [API] Response error:', {
      url,
      method: error.config?.method?.toUpperCase(),
      status,
      statusText: error.response?.statusText,
      errorMessage: error.message,
      authStatus: authTokenManager.getStatus()
    })

    // Passive service status detection: report infrastructure errors
    // 502/503/522 typically indicate database or server infrastructure issues
    if (status && [502, 503, 522].includes(status)) {
      console.warn('⚠️ [API] Infrastructure error detected - dispatching service status event')
      window.dispatchEvent(new CustomEvent('service-status-error', {
        detail: { service: 'database', status, url, message: error.message }
      }))
    }

    // Handle 401 errors with retry logic - attempt token refresh before redirecting
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      console.warn('🔄 [API] 401 received - attempting token refresh with skipCache')

      try {
        // Try to get a fresh token from Clerk, bypassing cache to force refresh
        // This recovers sessions where the cached token expired but the session is still valid
        const newToken = await authTokenManager.getToken({ skipCache: true })

        if (newToken) {
          // Token refreshed successfully - retry the request
          console.log('✅ [API] Token refreshed - retrying request')
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return api(originalRequest)
        } else {
          // No token available - user is truly signed out
          console.warn('🚫 [API] No token available after refresh - user signed out')

          if (shouldRedirectToSignInOnUnauthorized(window.location.pathname, false)) {
            window.location.href = '/sign-in'
          }
        }
      } catch (refreshError) {
        console.error('❌ [API] Token refresh failed:', refreshError)

        // Only redirect if user is definitely signed out
        if (shouldRedirectToSignInOnUnauthorized(window.location.pathname, authTokenManager.isUserSignedIn())) {
          window.location.href = '/sign-in'
        }
      }
    }

    return Promise.reject(error)
  }
)

console.log('🔧 [API] Axios interceptors configured at module load time')

// Custom hook to get authenticated API instance
export const useApi = () => {
  return api
}

// API Service Class
// Auth headers are automatically added by ApiProvider.tsx interceptor
class ApiService {

  // User endpoints
  async getProfile(): Promise<ApiResponse<User>> {
    const response = await api.get('/user/profile')
    return response.data
  }

  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    const response = await api.put('/user/profile', data)
    return response.data
  }

  async getAccountDeletionPreview(): Promise<ApiResponse<{ data: {
    user: { id: string; email: string; firstName?: string | null; lastName?: string | null }
    ownedWorkspaces: Array<{ id: string; organizationName?: string | null }>
    memberWorkspaces: Array<{ teamId: string; role?: string | null }>
    activeSubscriptions: Array<{ id: string; teamId: string | null; stripeSubscriptionId: string; status: string }>
    counts: {
      ownedWorkspaces: number
      memberWorkspaces: number
      domains: number
      pages: number
      schemaGenerations: number
      alternateFiles: number
      hubSpotConnections: number
      teamMembers: number
      pendingInvites: number
      siteScans: number
      botTrackingSites: number
      monitoringRecords: number
    }
    warnings: string[]
  } }>> {
    const response = await api.get('/user/account-deletion/preview')
    return response.data
  }

  async deleteAccount(data: {
    confirmEmail: string
    confirmWorkspaceDeletion: boolean
  }): Promise<ApiResponse<{ data: { deleted: true } }>> {
    const response = await api.delete('/user/account', { data })
    return response.data
  }

  async initializeUser(data: {
    email: string
    firstName?: string
    lastName?: string
    attribution?: {
      utm_source?: string
      utm_medium?: string
      utm_campaign?: string
      utm_term?: string
      utm_content?: string
      landing_path?: string
      captured_at?: string
    }
    /** True when the person signed up from a team invitation. */
    invited?: boolean
  }): Promise<ApiResponse<User>> {
    const response = await api.post('/user/init', data)
    return response.data
  }

  async getCredits(): Promise<ApiResponse<{
    creditBalance: number
    purchasedCredits: number
    subscriptionCredits: number
    totalCreditsUsed: number
  }>> {
    const response = await api.get('/user/credits')
    return response.data
  }

  async getCreditTransactions(page = 1, limit = 10): Promise<ApiResponse<PaginatedResponse<CreditTransaction>>> {
    const response = await api.get('/user/transactions', {
      params: { page, limit }
    })
    return response.data
  }

  async getUserStats(): Promise<ApiResponse<any>> {
    const response = await api.get('/user/stats')
    return response.data
  }

  async getSuccessPreviewStatus(): Promise<ApiResponse<{ hasSeenPreview: boolean }>> {
    const response = await api.get('/user/success-preview-status')
    return response.data
  }

  async getTourStatus(tourKey: string): Promise<ApiResponse<{
    tourKey: string
    dismissed: boolean
    dismissedAt: string | null
  }>> {
    const response = await api.get(`/user/tours/${encodeURIComponent(tourKey)}/status`)
    return response.data
  }

  async dismissTour(tourKey: string): Promise<ApiResponse<{
    tourKey: string
    dismissed: true
    dismissedAt: string
  }>> {
    const response = await api.post(`/user/tours/${encodeURIComponent(tourKey)}/dismiss`)
    return response.data
  }

  // The first-week guide. Served from the team's real tables on every request.
  async getFirstWeek(viewed?: 'checkout' | 'dashboard'): Promise<ApiResponse<FirstWeekResponse>> {
    const query = viewed ? `?viewed=${viewed}` : ''
    const response = await api.get(`/activation/first-week${query}`)
    return response.data
  }

  async answerFirstWeekBranch(branch: 'self' | 'team' | 'agency'): Promise<ApiResponse<{ branch: string }>> {
    const response = await api.post('/activation/first-week/branch', { branch })
    return response.data
  }

  // What AI agents can now read on a page that just went live, as sentences.
  async getLiveFacts(urlId: string): Promise<ApiResponse<{ facts: string[] }>> {
    const response = await api.get(`/activation/first-week/live-facts/${encodeURIComponent(urlId)}`)
    return response.data
  }

  async startOnboardingScan(domain: string): Promise<ApiResponse<{
    scan: {
      sessionId: string
      token: string
      domain: string
      scanId: string
      status: string
      expiresAt: string
    }
  }>> {
    const response = await api.post('/onboarding/scan', { domain })
    return response.data
  }

  async getOnboardingScanProgress(sessionId: string, token: string): Promise<ApiResponse<{
    progress: {
      status: string
      currentStep: string
      message: string
      pagesAnalyzed: number
      totalPages: number | null
      currentPageUrl?: string
      phase?: 'discovery' | 'crawling' | 'analyzing' | 'scoring'
      /** The site being scanned, from the session. */
      domain?: string
      errorCode?: string
      errorMessage?: string
      /** 'hubspot' or 'other' once the first page has been read. */
      platform?: 'hubspot' | 'other' | null
      /** The server's HUBSPOT_ONLY_ONBOARDING flag. Off means none of the HubSpot screens render. */
      hubspotOnly?: boolean
    }
  }>> {
    const response = await api.get(`/onboarding/scan/${sessionId}/progress`, {
      params: { t: token }
    })
    return response.data
  }

  async getOnboardingScanTeaser(sessionId: string, token: string): Promise<ApiResponse<{
    teaser: {
      sessionId: string
      domain: string
      status: string
      overallScore: number
      grade: string
      pagesAnalyzed: number
      pagesDiscovered: number
      issueCounts: {
        total: number
        critical: number
        high: number
        medium: number
        low: number
      }
      categoryScores: {
        access: number
        content: number
        structure: number
        schema: number
        alternate: number
      }
      expiresAt: string
      topOpportunity?: {
        url: string
        path: string
        findingTitle: string
        impactPoints: number
        effort: string | null
      } | null
    }
  }>> {
    const response = await api.get(`/onboarding/scan/${sessionId}/teaser`, {
      params: { t: token }
    })
    return response.data
  }

  async startOnboardingPreviewChecks(sessionId: string, token: string, options?: { correction?: boolean }): Promise<ApiResponse<{
    started: boolean
    limitState: 'ok' | 'cached' | 'rate_limited' | 'circuit_open'
    providers: Array<{ provider: 'openai' | 'anthropic' | 'gemini' | 'perplexity'; label: string }>
    results: Array<{
      id: string
      question: string
      provider: 'openai' | 'anthropic' | 'gemini' | 'perplexity'
      providerLabel: string
      status: 'pending' | 'running' | 'completed' | 'failed' | 'unavailable'
      answerSummary: string | null
      mentionsSite: boolean | null
      confidence: 'low' | 'medium' | 'high' | null
      errorMessage: string | null
      completedAt: string | null
    }>
    message?: string
  }>> {
    const response = await api.post(
      `/onboarding/scan/${sessionId}/preview-checks/start`,
      // correction:true tells the server the identity was edited — it drops the
      // session's rows and re-runs fresh (skipping domain cache and IP cap).
      options?.correction ? { correction: true } : undefined,
      { params: { t: token } }
    )
    return response.data
  }

  // NOTE: this GET always reports limitState 'ok' — callers must persist the
  // limitState returned by startOnboardingPreviewChecks (a rate_limited or
  // circuit_open start means there is nothing to poll for).
  async getOnboardingPreviewChecks(sessionId: string, token: string): Promise<ApiResponse<{
    limitState: 'ok' | 'cached' | 'rate_limited' | 'circuit_open'
    providers: Array<{ provider: 'openai' | 'anthropic' | 'gemini' | 'perplexity'; label: string }>
    results: Array<{
      id: string
      question: string
      provider: 'openai' | 'anthropic' | 'gemini' | 'perplexity'
      providerLabel: string
      status: 'pending' | 'running' | 'completed' | 'failed' | 'unavailable'
      answerSummary: string | null
      mentionsSite: boolean | null
      confidence: 'low' | 'medium' | 'high' | null
      errorMessage: string | null
      completedAt: string | null
    }>
  }>> {
    const response = await api.get(`/onboarding/scan/${sessionId}/preview-checks`, {
      params: { t: token }
    })
    return response.data
  }

  async getOnboardingAIContext(sessionId: string, token: string): Promise<ApiResponse<{
    context: {
      sessionId: string
      domain: string
      status: 'ready' | 'fallback' | 'failed'
      companyName: string
      businessCategory: string
      audience: string
      offerings: string[]
      possibleCompetitors: string[]
      suggestedQuestions: string[]
      overview: string
      source: 'ai' | 'fallback' | 'visitor_edit'
      updatedAt?: string | null
    }
  }>> {
    const response = await api.get(`/onboarding/scan/${sessionId}/ai-context`, {
      params: { t: token }
    })
    return response.data
  }

  async saveOnboardingAIContext(sessionId: string, token: string, payload: {
    companyName?: string
    businessCategory?: string
    audience?: string
    offerings?: string[]
    possibleCompetitors?: string[]
    questions?: string[]
  }): Promise<ApiResponse<{
    context: {
      sessionId: string
      domain: string
      status: 'ready' | 'fallback' | 'failed'
      companyName: string
      businessCategory: string
      audience: string
      offerings: string[]
      possibleCompetitors: string[]
      suggestedQuestions: string[]
      overview: string
      source: 'ai' | 'fallback' | 'visitor_edit'
      updatedAt?: string | null
    }
  }>> {
    const response = await api.put(`/onboarding/scan/${sessionId}/ai-context`, payload, {
      params: { t: token }
    })
    return response.data
  }

  async claimOnboardingScan(sessionId: string, token: string): Promise<ApiResponse<
    | {
        outcome?: 'claimed'
        claimedScanId: string
        domain: string
        domainSlug?: string
        domainId?: string
        domainRecord?: UnifiedDomain
        alreadyClaimed: boolean
      }
    | { outcome: 'existing_user'; hasSubscription: boolean }
  >> {
    const response = await api.post('/onboarding/claim', { sessionId, token })
    return response.data
  }

  async getOnboardingIntentProfile(params?: {
    scanId?: string | null
    domain?: string | null
  }): Promise<ApiResponse<{
    profile: OnboardingIntentProfile | null
    requiresIntent: boolean
  }>> {
    const response = await api.get('/onboarding/intent', {
      params: {
        scanId: params?.scanId || undefined,
        domain: params?.domain || undefined,
      },
    })
    return response.data
  }

  async saveOnboardingIntentProfile(payload: {
    scanId?: string | null
    domain?: string | null
    primaryGoal: OnboardingIntentProfile['primaryGoal']
    primaryGoalOther?: string | null
    websiteManager?: OnboardingIntentProfile['websiteManager']
    clientSiteCount?: OnboardingIntentProfile['clientSiteCount']
    depthPreference: OnboardingIntentProfile['depthPreference']
    discoverySource?: OnboardingIntentProfile['discoverySource']
    discoverySourceOther?: string | null
  }): Promise<ApiResponse<{
    profile: OnboardingIntentProfile
  }>> {
    const response = await api.post('/onboarding/intent', payload)
    return response.data
  }

  async getClaimedOnboardingAIContext(scanId: string): Promise<ApiResponse<{
    context: {
      sessionId: string
      domain: string
      status: 'ready' | 'fallback' | 'failed'
      companyName: string
      businessCategory: string
      audience: string
      offerings: string[]
      possibleCompetitors: string[]
      suggestedQuestions: string[]
      overview: string
      source: 'ai' | 'fallback' | 'visitor_edit'
      updatedAt?: string | null
    } | null
  }>> {
    const response = await api.get(`/onboarding/scan/${scanId}/ai-context/claimed`)
    return response.data
  }

  async startOnboardingAIAnswerChecks(scanId: string): Promise<ApiResponse<{
    started: boolean
    providers: Array<{ provider: 'openai' | 'anthropic' | 'gemini' | 'perplexity'; label: string }>
    results: Array<{
      id: string
      question: string
      provider: 'openai' | 'anthropic' | 'gemini' | 'perplexity'
      providerLabel: string
      status: 'pending' | 'running' | 'completed' | 'failed' | 'unavailable'
      answerSummary: string | null
      mentionsSite: boolean | null
      confidence: 'low' | 'medium' | 'high' | null
      errorMessage: string | null
      completedAt: string | null
    }>
    message?: string
  }>> {
    const response = await api.post(`/onboarding/scan/${scanId}/ai-answer-checks/start`)
    return response.data
  }

  async getOnboardingAIAnswerChecks(scanId: string): Promise<ApiResponse<{
    providers: Array<{ provider: 'openai' | 'anthropic' | 'gemini' | 'perplexity'; label: string }>
    results: Array<{
      id: string
      question: string
      provider: 'openai' | 'anthropic' | 'gemini' | 'perplexity'
      providerLabel: string
      status: 'pending' | 'running' | 'completed' | 'failed' | 'unavailable'
      answerSummary: string | null
      mentionsSite: boolean | null
      confidence: 'low' | 'medium' | 'high' | null
      errorMessage: string | null
      completedAt: string | null
    }>
  }>> {
    const response = await api.get(`/onboarding/scan/${scanId}/ai-answer-checks`)
    return response.data
  }

  // --- Onboarding fix flow (recall -> confirm profile -> live build -> handoff)

  async getOnboardingFixTarget(scanId: string): Promise<ApiResponse<OnboardingFixTargetResponse>> {
    const response = await api.get(`/onboarding/scan/${scanId}/fix-target`)
    return response.data
  }

  async getOnboardingFixProfile(scanId: string): Promise<ApiResponse<OnboardingFixProfileResponse>> {
    const response = await api.get(`/onboarding/scan/${scanId}/fix-profile`)
    return response.data
  }

  async saveOnboardingFixProfile(
    scanId: string,
    edits: OnboardingFixProfileEdits
  ): Promise<ApiResponse<{ saved: boolean }>> {
    const response = await api.put(`/onboarding/scan/${scanId}/fix-profile`, edits)
    return response.data
  }

  /**
   * Generates real schema for the one chosen page. The response carries only
   * the opening lines: the rest of the document is withheld on the server and
   * never reaches this client.
   */
  async createOnboardingFixPreview(scanId: string): Promise<ApiResponse<OnboardingFixPreviewResponse>> {
    const response = await api.post(`/onboarding/scan/${scanId}/fix-preview`)
    return response.data
  }

  async getOnboardingFacts(sessionId: string, token: string): Promise<ApiResponse<PageFactsSnapshot>> {
    const response = await api.get(`/onboarding/scan/${sessionId}/facts`, { params: { t: token } })
    return response.data
  }

  async getOnboardingStanding(): Promise<ApiResponse<OnboardingStanding>> {
    const response = await api.get('/onboarding/standing')
    return response.data
  }

  /** `page` is a page the person chose in place of ours: one of the addresses in the snapshot's `pages`, sent as it is there. The server refuses any other (400 PAGE_NOT_READ), and once a fix has been started for the scan it answers that fix's page whatever is named: read the answer's `url`. */
  async getClaimedOnboardingFacts(scanId: string, page?: string | null): Promise<ApiResponse<PageFactsSnapshot>> {
    const response = page
      ? await api.get(`/onboarding/scan/${scanId}/facts/claimed`, { params: { page } })
      : await api.get(`/onboarding/scan/${scanId}/facts/claimed`)
    return response.data
  }

  async startOnboardingPutLive(scanId: string, url: string): Promise<ApiResponse<PutLiveJob>> {
    const response = await api.post(`/onboarding/scan/${scanId}/put-live`, { url })
    return response.data
  }

  async getOnboardingPutLive(scanId: string): Promise<ApiResponse<PutLiveJob>> {
    const response = await api.get(`/onboarding/scan/${scanId}/put-live`)
    return response.data
  }

  /** Reads the live page again for a fix that is on HubSpot but was not confirmed yet. */
  async confirmOnboardingPutLive(scanId: string): Promise<ApiResponse<PutLiveJob>> {
    const response = await api.post(`/onboarding/scan/${scanId}/put-live/confirm`)
    return response.data
  }

  /**
   * Whether this account's first page is free for the page behind a claimed
   * scan, and the server's own word for a no when it has one. A yes names
   * the page it is for. Asking takes nothing: the press of Put it live is
   * what the server acts on.
   */
  async getOnboardingFreePage(scanId: string): Promise<ApiResponse<FreePageForScanAnswer>> {
    // Fifteen seconds, not the two minutes every other request is given: the
    // report's main button waits on this answer, and no answer is a no.
    const response = await api.get(`/onboarding/scan/${scanId}/free-page`, { timeout: 15000 })
    return response.data
  }

  /**
   * The same question asked by a page's address, for the ordinary page
   * detail, which holds no scan: is this page the team's free first page?
   * Asking takes nothing. The address goes whole, as the one query value
   * `url`, and the write that follows a yes must be started for that same
   * string. Fifteen seconds, like the question above: the page offers
   * nothing free until this answers, and no answer is a no.
   */
  async getOnboardingFreePageByAddress(url: string): Promise<ApiResponse<FreePageByAddressAnswer>> {
    const response = await api.get('/onboarding/free-page', { params: { url }, timeout: 15000 })
    return response.data
  }

  /**
   * Hands back the site a claimed scan was made for, so the account can
   * enter another: the site and everything held for it are removed, for
   * good. One request and the server's decision. It finds the site by the
   * scan, so no site is named here, and it refuses, with a sentence a person
   * may read, when the site has to stay: 403 NOT_TEAM_OWNER, 409
   * SITE_STAYS_PLAN, SITE_STAYS_FIX or SITE_STAYS_FREE_PAGE, 404
   * SITE_NOT_FOUND or SCAN_NOT_FOUND. 404 NOT_FOUND, with no sentence, is
   * sign-up first switched off.
   */
  async handBackOnboardingScan(scanId: string): Promise<ApiResponse<{ domain: string }>> {
    const response = await api.post(`/onboarding/scan/${scanId}/hand-back`)
    return response.data
  }

  /**
   * The funnel's own record of a moment only this page sees. The call site
   * swallows any failure: a milestone must never get in the way of the page.
   */
  async recordOnboardingMilestone(scanId: string, milestone: 'report_seen' | 'paywall_seen' | 'trial_checkout_completed', metadata?: { branch?: 'put_live' | 'without_fix' | 'without_connect'; tier?: string }): Promise<void> {
    await api.post(`/onboarding/scan/${scanId}/milestone`, { milestone, metadata })
  }

  /**
   * The paywall met inside the app by an account with no plan: recorded
   * against the account, with no scan to key it to. The endpoint is there
   * only while sign-up first is switched on. The call site swallows any
   * failure.
   */
  async recordAccountMilestone(milestone: 'paywall_seen', metadata?: { source: 'billing_plans' | 'checkout'; tier?: string }): Promise<void> {
    await api.post('/onboarding/milestone', { milestone, metadata })
  }

  /** The gate, reached before there is an account: reported by the session id alone. */
  async recordAnonymousOnboardingMilestone(sessionId: string, milestone: 'gate_seen'): Promise<void> {
    await api.post(`/onboarding/session/${sessionId}/milestone`, { milestone })
  }

  async ensureOnboardingScanDomain(scanId: string): Promise<ApiResponse<{
    domain: string
    domainSlug: string
    domainId: string
    domainRecord: UnifiedDomain
  }>> {
    const response = await api.post(`/onboarding/scan/${scanId}/ensure-domain`)
    return response.data
  }

  async markOnboardingResultsViewed(scanId: string): Promise<ApiResponse<{
    tracked: boolean
    scanId: string
  }>> {
    const response = await api.post(`/onboarding/scan/${scanId}/results-viewed`)
    return response.data
  }

  async markSuccessPreviewSeen(): Promise<ApiResponse<{ success: boolean }>> {
    const response = await api.post('/user/success-preview-seen')
    return response.data
  }

  // Schema generation endpoints
  async generateSchema(url: string, options?: any): Promise<ApiResponse<{
    schemas: JsonLdSchema[]
    metadata: any
    validationResults: any[]
    htmlScriptTags?: string
    schemaScore?: any
    urlId?: string
    publisherUsed?: { id: string; name: string }
  }>> {
    // Extract schemaType from options.requestedSchemaTypes for backend
    const schemaType = options?.requestedSchemaTypes?.[0] || 'Auto'

    const response = await api.post('/schema/generate', {
      url,
      schemaType,  // Send at top level for backend
      options
    })
    return response.data
  }

  // Async schema generation (background job) endpoints
  async generateSchemaAsync(url: string, options?: any): Promise<ApiResponse<{
    jobId: string
    status: 'pending'
    pollInterval: number
  }>> {
    const schemaType = options?.requestedSchemaTypes?.[0] || 'Auto'
    // Pull `regenerate` out of options and lift to the top level so the
    // backend (which reads req.body.regenerate) can skip the duplicate-check
    // when the user is intentionally replacing an existing schema.
    const regenerate = options?.regenerate === true
    const forceFresh = options?.forceFresh === true
    const response = await api.post('/schema/generate-async', {
      url,
      schemaType,
      options,
      regenerate,
      forceFresh,
    })
    return response.data
  }

  async getJobStatus(jobId: string): Promise<ApiResponse<{
    jobId: string
    url: string
    status: 'pending' | 'processing' | 'complete' | 'failed' | 'cancelled'
    progress: number
    progressMessage: string | null
    result?: {
      schemas: JsonLdSchema[]
      htmlScriptTags?: string
      metadata: any
      validationResults: any[]
      schemaScore?: any
      urlId?: string
    }
    error?: {
      code: string
      message: string
      suggestedAlternatives?: string[]
      requestedType?: string
    }
    createdAt: string
    startedAt: string | null
    completedAt: string | null
  }>> {
    const response = await api.get(`/schema/jobs/${jobId}`)
    return response.data
  }

  async cancelJob(jobId: string): Promise<ApiResponse<{
    success: boolean
    message: string
  }>> {
    const response = await api.delete(`/schema/jobs/${jobId}`)
    return response.data
  }

  async listJobs(options?: { limit?: number; status?: string }): Promise<ApiResponse<{
    jobs: Array<{
      id: string
      url: string
      status: string
      progress: number
      progressMessage: string | null
      createdAt: string
    }>
    total: number
  }>> {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.status) params.append('status', options.status)
    const response = await api.get(`/schema/jobs?${params.toString()}`)
    return response.data
  }

  async validateSchema(schema: JsonLdSchema, strict = false): Promise<ApiResponse<{
    isValid: boolean
    errors: any[]
    warnings: any[]
    schema?: JsonLdSchema
  }>> {
    const response = await api.post('/schema/validate', {
      schema,
      strict
    })
    return response.data
  }

  async validateMultipleSchemas(schemas: JsonLdSchema[]): Promise<ApiResponse<{
    isValid: boolean
    results: any[]
    summary: any
  }>> {
    const response = await api.post('/schema/validate-multiple', {
      schemas
    })
    return response.data
  }

  async refineSchema(schemas: JsonLdSchema[], url: string, options?: any, schemaId?: string): Promise<ApiResponse<{
    schemas: JsonLdSchema[]
    htmlScriptTags?: string
    schemaScore: any
    metadata: any
    refinementCount?: number
    remainingRefinements?: number
    highlightedChanges?: any[]
    refinementSkipped?: boolean
    refinementSkippedReason?: string
    refinementOutcome?: 'applied' | 'fallback_applied' | 'skipped' | 'limit_reached'
    rejectedSchemaScore?: any
  }>> {
    const response = await api.post('/schema/refine', {
      schemas,
      url,
      options,
      schemaId
    })
    return response.data
  }

  async refineLibrarySchema(schemaId: string, schemas: JsonLdSchema[], url: string, options?: any): Promise<ApiResponse<{
    schemas: JsonLdSchema[]
    htmlScriptTags?: string
    schemaScore: any
    metadata: any
    highlightedChanges: string[]
    refinementCount: number
    remainingRefinements: number
    refinementSkipped?: boolean
    refinementSkippedReason?: string
    refinementOutcome?: 'applied' | 'fallback_applied' | 'skipped' | 'limit_reached'
    rejectedSchemaScore?: any
  }>> {
    const response = await api.post('/schema/refine-library', {
      schemaId,
      schemas,
      url,
      options
    })
    return response.data
  }

  async recalculateScore(schemaId: string, schemas: JsonLdSchema[]): Promise<ApiResponse<{
    schemaScore: any
  }>> {
    const response = await api.post('/schema/recalculate-score', {
      schemaId,
      schemas
    })
    return response.data
  }

  async getGenerationHistory(page = 1, limit = 10): Promise<ApiResponse<PaginatedResponse<SchemaGenerationResult>>> {
    const response = await api.get('/schema/history', {
      params: { page, limit }
    })
    return response.data
  }

  async getGenerationStats(): Promise<ApiResponse<any>> {
    const response = await api.get('/schema/stats')
    return response.data
  }

  async getGenerationInsights(timeframe: 'day' | 'week' | 'month' = 'week'): Promise<ApiResponse<any>> {
    const response = await api.get('/schema/insights', {
      params: { timeframe }
    })
    return response.data
  }

  async extractSchemaFromUrl(url: string): Promise<ApiResponse<{
    url: string
    schemas: JsonLdSchema[]
    schemasFound: number
    metadata: {
      title: string
      description: string
    }
  }>> {
    const response = await api.post('/schema/extract', {
      url
    })
    return response.data
  }

  // Credit pack endpoints
  async getCreditPacks(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/payment/credit-packs')
    return response.data
  }

  async createPaymentIntent(creditPackId: string): Promise<ApiResponse<{
    clientSecret: string
    paymentIntentId: string
  }>> {
    const response = await api.post('/payment/create-intent', {
      creditPackId
    })
    return response.data
  }

  async confirmPayment(paymentIntentId: string): Promise<ApiResponse<{
    success: boolean
    message: string
    creditsAdded?: number
  }>> {
    const response = await api.post('/payment/confirm', {
      paymentIntentId
    })
    return response.data
  }

  async getPaymentHistory(page = 1, limit = 10): Promise<ApiResponse<PaginatedResponse<any>>> {
    const response = await api.get('/payment/history', {
      params: { page, limit }
    })
    return response.data
  }

  async getPaymentConfig(): Promise<ApiResponse<{ publishableKey: string }>> {
    const response = await api.get('/payment/config')
    return response.data
  }

  // Model testing endpoints
  async getAvailableModels(): Promise<ApiResponse<{
    models: Array<{
      value: string
      label: string
      description: string
      cost: string
      capabilities: string[]
      recommended?: boolean
      experimental?: boolean
    }>
    currentModel: string
  }>> {
    const response = await api.get('/model-test/models')
    return response.data
  }

  async testModelPerformance(model: string, url: string, options?: any): Promise<ApiResponse<{
    model: string
    responseTime: number
    schemas: JsonLdSchema[]
    metadata: any
    validationResults: any[]
    schemaScore?: any
  }>> {
    const response = await api.post('/model-test/test', {
      model,
      url,
      options
    })
    return response.data
  }

  async switchModel(model: string): Promise<ApiResponse<{
    model: string
    message: string
  }>> {
    const response = await api.post('/model-test/switch', {
      model
    })
    return response.data
  }

  // Schema Manager endpoints
  async getUserDomains(): Promise<ApiResponse<UserDomain[]>> {
    const response = await api.get('/library/domains')
    return response.data
  }

  async getUserUrls(filters?: UrlLibraryFilters): Promise<ApiResponse<DiscoveredUrl[]>> {
    const response = await api.get('/library/urls', {
      params: filters
    })
    return response.data
  }

  async hideUrl(urlId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.put(`/library/urls/${urlId}/hide`)
    return response.data
  }

  async unhideUrl(urlId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.put(`/library/urls/${urlId}/unhide`)
    return response.data
  }

  async deleteDomain(domainId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/library/domains/${domainId}`)
    return response.data
  }

  async getUrlSchema(urlId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/library/urls/${urlId}/schema`)
    return response.data
  }

  async getAllUrlSchemas(urlId: string): Promise<ApiResponse<any[]>> {
    console.log('📡 API: Fetching schemas for URL ID:', urlId)
    const response = await api.get(`/library/urls/${urlId}/schemas`)
    console.log('📡 API: Received schemas response:', {
      requestedUrlId: urlId,
      schemasCount: response.data.data?.length || 0,
      schemaUrls: response.data.data?.map((s: any) => s.url)
    })
    return response.data
  }

  async updateUrlSchema(urlId: string, schemas: any[]): Promise<ApiResponse<{ message: string }>> {
    const response = await api.put(`/library/urls/${urlId}/schema`, { schemas })
    return response.data
  }

  async deleteUrl(urlId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/library/urls/${urlId}`)
    return response.data
  }

  async deleteSchemaType(schemaId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/schema/${schemaId}`)
    return response.data
  }

  async updateSchemaContent(schemaId: string, schemas: JsonLdSchema[]): Promise<ApiResponse<{ schemas: JsonLdSchema[] }>> {
    const response = await api.put(`/schema/${schemaId}`, { schemas })
    return response.data
  }

  async checkUrlExists(url: string): Promise<ApiResponse<{
    exists: boolean
    urlId?: string
    createdAt?: string
    hasSchema: boolean
  }>> {
    const response = await api.get('/library/check-url', {
      params: { url }
    })
    return response.data
  }

  async checkUrlManaged(url: string): Promise<ApiResponse<{ isManaged: boolean }>> {
    const response = await api.get('/library/check-managed', {
      params: { url }
    })
    return response.data
  }

  // Admin endpoints
  async getPlatformStats(): Promise<ApiResponse<{
    totalUsers: number
    activeUsers: number
    totalSchemas: number
    totalCreditsDistributed: number
    totalCreditsUsed: number
    totalRevenue: number
    revenueThisMonth: number
    creditPackRevenueTotal: number
    creditPackRevenueThisMonth: number
    subscriptionRevenueTotal: number
    subscriptionRevenueThisMonth: number
    refundsTotal: number
    refundsThisMonth: number
    payingCustomers: number
    conversionRate: number
    newUsersThisWeek: number
    newUsersThisMonth: number
    schemasToday: number
    schemasThisWeek: number
    averageCreditsPerUser: number
    creditPackBreakdown: Array<{ packId: string; packName: string; purchases: number; revenue: number }>
  }>> {
    const response = await api.get('/admin/stats')
    return response.data
  }

  async searchUsers(query: string): Promise<ApiResponse<User[]>> {
    const response = await api.get('/admin/users/search', {
      params: { query }
    })
    return response.data
  }

  async getAllUsers(limit = 50, offset = 0): Promise<ApiResponse<User[]>> {
    const response = await api.get('/admin/users', {
      params: { limit, offset }
    })
    return response.data
  }

  async getUserDetails(userId: string): Promise<ApiResponse<{
    user: User
    activity: any[]
    stats: any
    subscription?: any
    teamSubscriptions?: any[]
    paidTeamSubscriptions?: any[]
    engagement?: any
    teams?: any
    workspaceSummary?: any
    activationSummary?: any
    billingSummary?: any
    engagementHealth?: any
    supportSignals?: any[]
    billingInvoiceScopes?: any[]
    onboardingIntentProfile?: OnboardingIntentProfile | null
  }>> {
    const response = await api.get(`/admin/users/${userId}`)
    return response.data
  }

  async getAdminEngagementAccounts(segment = 'all', limit = 100): Promise<ApiResponse<{
    segment: string
    accounts: Array<{
      teamId: string
      ownerUserId: string | null
      ownerEmail: string | null
      teamName: string | null
      accountMode: string
      planTier: string | null
      effectiveTier: string | null
      lifecycleStage: string
      riskLevel: string
      activationScore: number
      engagementScore: number
      valueScore: number
      riskScore: number
      expansionIntentScore: number
      likelyBlocker: string | null
      recommendedActions: string[]
      lastEngagedAt: string | null
      computedAt: string
      updatedAt: string
    }>
  }>> {
    const response = await api.get('/admin/engagement/accounts', {
      params: { segment, limit },
    })
    return response.data
  }

  async deleteUserCompletely(userId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/admin/users/${userId}`)
    return response.data
  }

  async modifyUserCredits(userId: string, amount: number, reason: string): Promise<ApiResponse<{
    data: User
    message: string
  }>> {
    const response = await api.post('/admin/users/credits', {
      userId,
      amount,
      reason
    })
    return response.data
  }

  async toggleAdminStatus(userId: string, isAdmin: boolean): Promise<ApiResponse<{
    data: User
    message: string
  }>> {
    const response = await api.patch('/admin/users/admin-status', {
      userId,
      isAdmin
    })
    return response.data
  }

  // Admin-granted subscription access endpoints

  async grantProAccess(
    userId: string,
    durationMonths: number,
    notes?: string,
    planTier: 'essential' | 'growth' | 'portfolio' = 'growth'
  ): Promise<ApiResponse<GrantedProAccess>> {
    const response = await api.post(`/admin/users/${userId}/grant-pro`, {
      planTier,
      durationMonths,
      notes
    })
    return response.data
  }

  async revokeProAccess(userId: string): Promise<ApiResponse<GrantedProAccess>> {
    const response = await api.delete(`/admin/users/${userId}/grant-pro`)
    return response.data
  }

  async getGrantHistory(userId: string): Promise<ApiResponse<GrantHistoryResponse>> {
    const response = await api.get(`/admin/users/${userId}/grant-pro`)
    return response.data
  }

  // Invoice preview admin endpoint
  async previewUserInvoices(userId: string): Promise<ApiResponse<{
    userId: string
    stripeCustomerId: string
    invoices: Array<{
      stripeInvoiceId: string
      description: string
      amountPaid: number
      currency: string
      status: string | null
      periodStart: string | null
      periodEnd: string | null
      created: string | null
      invoicePdfUrl: string | null
      isNew: boolean
    }>
    summary: {
      total: number
      new: number
      existing: number
    }
  }>> {
    const response = await api.get(`/admin/users/${userId}/preview-invoices`)
    return response.data
  }

  // Invoice sync admin endpoint
  async syncUserInvoices(userId: string): Promise<ApiResponse<{
    userId: string
    stripeCustomerId: string
    totalInvoicesFound: number
    synced: number
    skipped: number
    errors?: string[]
  }>> {
    const response = await api.post(`/admin/users/${userId}/sync-invoices`)
    return response.data
  }

  async getUserInvoiceScopes(userId: string): Promise<ApiResponse<{
    userId: string
    scopes: Array<{
      scope: any
      invoices: any[]
      reconciliation: any
    }>
  }>> {
    const response = await api.get(`/admin/users/${userId}/invoices`)
    return response.data
  }

  async reconcileUserInvoiceScopes(userId: string): Promise<ApiResponse<{
    userId: string
    scopes: Array<{
      scope: any
      invoices: any[]
      reconciliation: any
    }>
    summary: {
      scopesChecked: number
      insertedOrRepaired: number
      alreadyPresent: number
      failed: number
    }
  }>> {
    const response = await api.post(`/admin/users/${userId}/reconcile-invoices`)
    return response.data
  }

  async backfillAllInvoices(): Promise<ApiResponse<{
    totalSubscriptions?: number
    totalUsers: number
    totalSynced: number
    totalSkipped: number
    totalErrors: number
    creditPackProcessed?: number
    creditPackErrors?: number
  }>> {
    const response = await api.post('/admin/backfill-invoices')
    return response.data
  }

  // Overhead expenses
  async getOverheadExpenses(month?: string): Promise<ApiResponse<any[]>> {
    const params = month ? { month } : {}
    const response = await api.get('/admin/overhead', { params })
    return response.data
  }

  async upsertOverheadExpense(data: {
    month: string, category: string, label: string,
    amount_cents: number, is_fixed?: boolean, notes?: string
  }): Promise<ApiResponse<any>> {
    const response = await api.post('/admin/overhead', data)
    return response.data
  }

  async deleteOverheadExpense(id: string): Promise<ApiResponse<any>> {
    const response = await api.delete(`/admin/overhead/${id}`)
    return response.data
  }

  async getStripeFees(month?: string): Promise<ApiResponse<any>> {
    const params = month ? { month } : {}
    const response = await api.get('/admin/overhead/stripe-fees', { params })
    return response.data
  }

  async getProfitSummary(month?: string): Promise<ApiResponse<any>> {
    const params = month ? { month } : {}
    const response = await api.get('/admin/overhead/profit-summary', { params })
    return response.data
  }

  // Admin — Audit Request Management
  async getAuditRequests(params?: { status?: string; source?: string; tier?: string; search?: string; limit?: number; offset?: number }): Promise<ApiResponse<{ requests: any[]; total: number }>> {
    const response = await api.get('/admin/audits', { params })
    return response.data
  }

  async getAuditRequestStats(): Promise<ApiResponse<any>> {
    const response = await api.get('/admin/audits/stats')
    return response.data
  }

  async getAuditRequest(id: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/admin/audits/${id}`)
    return response.data
  }

  async updateAuditRequest(id: string, data: { status?: string; admin_notes?: string; assigned_to?: string; debrief_delivered?: boolean }): Promise<ApiResponse<any>> {
    const response = await api.patch(`/admin/audits/${id}`, data)
    return response.data
  }

  // Audit checkout (public, no auth)
  async createAuditPaymentIntent(data: {
    name: string, email: string, company: string, website: string,
    usesHubspot: boolean, includeDebrief: boolean, packageId?: 'core' | 'portfolio'
  }): Promise<ApiResponse<{ clientSecret: string, paymentIntentId: string, auditRequestId: string }>> {
    const response = await api.post('/audit/create-payment-intent', data)
    return response.data
  }

  async confirmAuditPayment(data: {
    paymentIntentId: string, auditRequestId: string
  }): Promise<ApiResponse<any>> {
    const response = await api.post('/audit/confirm-payment', data)
    return response.data
  }

  async submitAuditIntake(data: {
    auditRequestId: string, email: string, brands: string,
    webProperties: string, priorities: string, notes: string
  }): Promise<ApiResponse<any>> {
    const response = await api.post('/audit/submit-intake', data)
    return response.data
  }

  // Admin cancel subscription endpoint
  async adminCancelSubscription(userId: string, immediately: boolean = false): Promise<ApiResponse<{
    message: string
    subscription: any
  }>> {
    const response = await api.post(`/admin/users/${userId}/cancel-subscription`, { immediately })
    return response.data
  }

  // Error logging admin endpoints
  async getErrorLogs(params?: {
    limit?: number
    offset?: number
    status?: 'open' | 'investigating' | 'resolved' | 'ignored'
    userId?: string
    errorType?: string
    startDate?: string
    endDate?: string
  }): Promise<ApiResponse<any[]> & { total: number; limit: number; offset: number }> {
    const response = await api.get('/admin/errors', { params })
    return response.data
  }

  async getErrorLog(id: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/admin/errors/${id}`)
    return response.data
  }

  async updateErrorLogStatus(id: string, status: string, resolutionNotes?: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.patch(`/admin/errors/${id}/status`, {
      status,
      resolutionNotes
    })
    return response.data
  }

  async batchUpdateErrorStatus(ids: string[], status: string): Promise<ApiResponse<{ updated: number; failed: number }>> {
    const response = await api.post('/admin/errors/batch-status', { ids, status })
    return response.data
  }

  async batchDeleteErrors(ids: string[]): Promise<ApiResponse<{ deleted: number; failed: number }>> {
    const response = await api.post('/admin/errors/batch-delete', { ids })
    return response.data
  }

  async getErrorStats(timeframe?: '24h' | '7d' | '30d'): Promise<ApiResponse<{
    total: number
    byType: Record<string, number>
    byStatus: Record<string, number>
  }>> {
    const response = await api.get('/admin/errors/stats', {
      params: { timeframe }
    })
    return response.data
  }

  async dispatchErrorToAgent(id: string, mode: 'diagnosis' | 'fix' = 'fix'): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post(`/admin/errors/${id}/dispatch-agent`, { mode })
    return response.data
  }

  // Agent fix pipeline endpoints
  async getAgentFixRuns(params?: {
    limit?: number
    offset?: number
    status?: string
  }): Promise<ApiResponse<any[]> & { total: number; limit: number; offset: number }> {
    const response = await api.get('/admin/agent-fixes', { params })
    return response.data
  }

  async getAgentFixStats(): Promise<ApiResponse<{
    total: number
    pending: number
    dispatched: number
    running: number
    pr_open: number
    queued: number
    merging: number
    conflict: number
    rebase_failed: number
    merged: number
    failed: number
    skipped: number
  }>> {
    const response = await api.get('/admin/agent-fixes/stats')
    return response.data
  }

  async getAgentMergeQueue(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/admin/agent-fixes/queue')
    return response.data
  }

  async getApiHealthMetrics(): Promise<ApiResponse<{
    current: {
      status: 'healthy' | 'degraded' | 'down'
      errorRate: number
      loggedProviderEvents?: number
      overloadEvents?: number
      failedRequests?: number
      successRate?: number
      avgResponseTime: number
    }
    last24Hours: {
      total529Errors: number
      totalApiErrors: number
      totalRequests: number
      successfulRequests: number
      failedRequests: number
      successRate: number
      usersAffected: number
      errorBreakdown: {
        error529: number
        error429: number
        error500: number
        other: number
      }
    }
    trends: {
      hourly: Array<{
        hour: string
        errors529: number
        totalErrors: number
        totalRequests: number
        successRate: number
      }>
    }
  }>> {
    const response = await api.get('/admin/api-health')
    return response.data
  }

  async getHubSpotStats(): Promise<ApiResponse<{
    totalConnections: number
    activeConnections: number
    connectionsByRegion: {
      na1: number
      eu1: number
      ap1: number
    }
    recentSyncs24h: number
    recentSyncFailures24h: number
    topUsersByConnections: Array<{
      userId: string
      userEmail: string
      connectionCount: number
    }>
  }>> {
    const response = await api.get('/admin/hubspot/stats')
    return response.data
  }

  // Chief of Staff (platform admins plus entitled pilot accounts)

  /**
   * Cheap gate probe: may this account use the chief? Answers 200 either way,
   * including "no", so an ordinary customer does not take a 403 on every page
   * load. Deliberately separate from the briefing, which is expensive. `state`
   * is the source of truth; `hasAccess` is kept only so a client cached from
   * before the state field shipped keeps working.
   */
  async getChiefAccess(): Promise<ApiResponse<{ state: ChiefAccessState; hasAccess: boolean; recoEnabled?: boolean }>> {
    const response = await api.get('/chief/access')
    return response.data
  }

  async optInToChief(): Promise<ApiResponse<{ state: ChiefAccessState }>> {
    const response = await api.post('/chief/opt-in')
    return response.data
  }

  async optOutOfChief(): Promise<ApiResponse<{ state: ChiefAccessState }>> {
    const response = await api.post('/chief/opt-out')
    return response.data
  }

  async getChiefBetaParticipants(): Promise<ApiResponse<ChiefBetaParticipant[]>> {
    const response = await api.get('/admin/chief/beta')
    return response.data
  }

  /**
   * The lifecycle emails and every sentence of them a person may rewrite.
   *
   * The preview goes through the server's own renderer, so what the editor
   * draws and what lands in an inbox cannot disagree. A save that is refused
   * comes back as a 400 whose `error` is a sentence written for the person
   * editing, so the caller shows it rather than replacing it.
   */
  async getLifecycleEmails(): Promise<ApiResponse<{
    kinds: Array<{
      kind: string
      label: string
      trigger: string
      honoursOptOut: boolean
      hasVariants: boolean
      slots: Array<{
        field: string
        label: string
        hint: string | null
        multiline: boolean
        tokens: string[]
        default: string
        current: string
        isOverridden: boolean
      }>
    }>
  }>> {
    const response = await api.get('/admin/emails')
    return response.data
  }

  async previewLifecycleEmail(payload: {
    kind: string
    variant?: string
    draft: Record<string, string>
  }): Promise<ApiResponse<{ subject: string; html: string; text: string }>> {
    const response = await api.post('/admin/emails/preview', payload)
    return response.data
  }

  async saveLifecycleEmailCopy(kind: string, field: string, value: string): Promise<ApiResponse<{ kind: string; field: string }>> {
    const response = await api.put(`/admin/emails/${kind}/${encodeURIComponent(field)}`, { value })
    return response.data
  }

  async revertLifecycleEmailCopy(kind: string, field: string): Promise<ApiResponse<{ kind: string; field: string }>> {
    const response = await api.delete(`/admin/emails/${kind}/${encodeURIComponent(field)}`)
    return response.data
  }

  async getLifecycleEmailHistory(kind: string): Promise<ApiResponse<{
    kind: string
    history: Array<{
      field: string
      previousValue: string | null
      newValue: string | null
      changedBy: string | null
      changedAt: string
    }>
  }>> {
    const response = await api.get(`/admin/emails/${kind}/history`)
    return response.data
  }

  async getLifecycleEmailLedger(): Promise<ApiResponse<{
    sends: Array<{ kind: string; email: string | null; sentAt: string; resendMessageId: string | null }>
  }>> {
    const response = await api.get('/admin/emails/ledger')
    return response.data
  }

  /*
    `data: 'real'` rebuilds the email from the signed-in admin's own account
    rather than the sample fixture, so the test answers whether the tokens
    resolve and not only whether the wording reads.
  */
  async sendLifecycleTestEmail(
    kind: string,
    data: 'sample' | 'real' = 'sample',
  ): Promise<ApiResponse<{ sent: boolean; to: string; kind: string; usedRealData: boolean }>> {
    const response = await api.post('/admin/emails/test-send', { kind, data })
    return response.data
  }

  /**
   * What the nightly sweep found on customers' live pages. Read-only: the
   * verdict data is new and covers the fleet over four nights, so it is worth
   * watching before anything acts on it.
   */
  async getHeldDriftSummary(): Promise<ApiResponse<import('@/types/heldDrift').HeldDriftSummary>> {
    const response = await api.get('/admin/monitoring/held-drift')
    return response.data
  }

  async getDeploymentHealth(): Promise<ApiResponse<DeploymentHealthOverview>> {
    const response = await api.get('/admin/deployment-health')
    return response.data
  }

  /**
   * `host` pins which domain the briefing reports on. The server ignores any
   * host the account does not own, so a stale saved value is harmless.
   */
  async getChiefBriefing(host?: string | null): Promise<ApiResponse<ChiefBriefing>> {
    const response = await api.get('/chief/briefing', host ? { params: { host } } : undefined)
    return response.data
  }

  async startChiefRun(
    pages: Array<{ urlId: string; url: string }>
  ): Promise<ApiResponse<{ runId: string }>> {
    const response = await api.post('/chief/execute', { pages })
    return response.data
  }

  async getChiefRun(runId: string): Promise<ApiResponse<ChiefRun>> {
    const response = await api.get(`/chief/runs/${runId}`)
    return response.data
  }

  async sendChiefFeedback(
    urlId: string,
    action: 'dismiss' | 'rescue' | 'grade_good' | 'grade_bad'
  ): Promise<ApiResponse<unknown>> {
    const response = await api.post('/chief/feedback', { urlId, action })
    return response.data
  }

  async markMomentSeen(momentKey: 'first_ai_visit'): Promise<ApiResponse<{ success: boolean }>> {
    const response = await api.post('/chief/moments/seen', { momentKey })
    return response.data
  }

  // Domain activation routine: dismiss (snooze) a gated checkpoint the chief prompts for but cannot perform.
  async snoozeChiefActivationStep(host: string, step: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await api.post('/chief/activation/snooze', { host, step })
    return response.data
  }

  // Business profile intake: the customer's one review of the profile the chief
  // drafted from their own site. `mode` decides whose values these are, and the
  // server treats them differently: 'confirm' never overwrites a value the
  // customer already typed, 'edit' applies their own deliberate changes.
  async confirmChiefProfileIntake(
    host: string,
    values: Record<string, unknown>,
    mode: 'confirm' | 'edit' = 'confirm'
  ): Promise<
    ApiResponse<{
      verified: boolean
      publishedPages: number
      /**
       * How many of publishedPages this refresh run actually covers. The
       * autonomous batch is bounded server-side, so on a large site this is the
       * smaller number, and it is the one the receipt is allowed to claim.
       */
      pagesThisRun?: number
      refreshStarted: boolean
      /** Recognized keys the customer sent content for that the server did not store. */
      droppedKeys?: string[]
      /**
       * Official-profile links the server refused, with the reason. Reported per
       * link because a partly kept list leaves droppedKeys empty, and a link
       * that quietly vanished is exactly the failure this endpoint exists to
       * avoid.
       */
      rejectedSameAs?: Array<{ url: string; reason: string }>
    }>
  > {
    const response = await api.post('/chief/intake/profile/confirm', { host, values, mode })
    return response.data
  }

  async snoozeChiefProfileIntake(host: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await api.post('/chief/intake/profile/snooze', { host })
    return response.data
  }

  // Proactive Chief nudges (in-app; dogfood-gated server-side)
  async getChiefNudges(host?: string | null): Promise<ApiResponse<{ nudges: ChiefNudge[] }>> {
    // Takes the same ?host= scope as the briefing: a nudge names a page, so a
    // briefing narrowed to one site must not be interrupted by another site's.
    const response = await api.get('/chief/nudges', host ? { params: { host } } : undefined)
    return response.data
  }

  async dismissChiefNudge(id: string): Promise<ApiResponse<unknown>> {
    const response = await api.post(`/chief/nudges/${id}/dismiss`)
    return response.data
  }

  async actChiefNudge(id: string): Promise<ApiResponse<unknown>> {
    const response = await api.post(`/chief/nudges/${id}/act`)
    return response.data
  }

  // Autonomy dial (Autonomy Dial V1): stored per account; the server env gate controls acting.
  async getChiefAutonomy(): Promise<ApiResponse<{ mode: 'ask' | 'act' }>> {
    const response = await api.get('/chief/autonomy')
    return response.data
  }

  async setChiefAutonomy(mode: 'ask' | 'act'): Promise<ApiResponse<{ mode: string }>> {
    const response = await api.put('/chief/autonomy', { mode })
    return response.data
  }

  async rollbackChiefRun(
    runId: string
  ): Promise<ApiResponse<{ pages: Array<{ urlId: string; rolledBack: boolean; skipped?: string }> }>> {
    const response = await api.post(`/chief/runs/${runId}/rollback`)
    return response.data
  }

  // Activity timeline (Activity Timeline V1): the chief's recorded trail, lazy-loaded on disclosure open.
  async getChiefTimeline(): Promise<ApiResponse<{ events: ChiefTimelineEvent[] }>> {
    const response = await api.get('/chief/timeline')
    return response.data
  }

  // Grounded chat (flag-gated server-side; the rail renders it only when briefing.chatEnabled).
  async chiefChat(
    question: string,
    history: Array<{ role: 'user' | 'chief'; text: string }>
  ): Promise<ApiResponse<ChiefChatResponse>> {
    const response = await api.post('/chief/chat', { question, history })
    return response.data
  }

  // Chat history: the account's last 20 turns within 7 days, oldest-first
  // (Chat V2 persistence). The rail rehydrates its thread from this on mount.
  async getChiefChatHistory(): Promise<ApiResponse<{ messages: ChiefChatHistoryMessage[] }>> {
    const response = await api.get('/chief/chat/history')
    return response.data
  }

  // Recommendation Report (dogfood-gated; see recoSamplingService for mode/allow-list logic)
  async seedReco(host: string): Promise<ApiResponse<unknown>> {
    const response = await api.post('/chief/reco/seed', { host })
    return response.data
  }

  async startRecoRun(host: string): Promise<ApiResponse<{ runId: string }>> {
    const response = await api.post('/chief/reco/run', { host })
    return response.data
  }

  async getRecoReport(host: string): Promise<ApiResponse<RecoReport>> {
    const response = await api.get('/chief/reco/report', { params: { host } })
    return response.data
  }

  // Schema failure tracking endpoints (Phase 1: Enhanced Failure Tracking)
  async getSchemaFailures(params?: {
    page?: number
    limit?: number
    failureReason?: string
    failureStage?: string
    userId?: string
    startDate?: string
    endDate?: string
  }): Promise<ApiResponse<{
    failures: Array<{
      id: string
      userId: string
      userEmail: string
      url: string
      failureReason: string
      failureStage: string
      errorMessage: string
      aiModelProvider: string
      stackTrace: string
      requestContext: any
      processingTimeMs: number
      createdAt: string
    }>
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }>> {
    const response = await api.get('/admin/schema-failures', { params })
    return response.data
  }

  async getSchemaFailureStats(params?: {
    startDate?: string
    endDate?: string
  }): Promise<ApiResponse<{
    totalFailures: number
    failuresByReason: Array<{ reason: string; count: number; percentage: number }>
    failuresByStage: Array<{ stage: string; count: number; percentage: number }>
    failuresByAiModel: Array<{ model: string; count: number; percentage: number }>
    recentFailures24h: number
    failureRate: number
    topFailingUrls: Array<{ url: string; count: number }>
    affectedUsers: number
  }>> {
    const response = await api.get('/admin/schema-failures/stats', { params })
    return response.data
  }

  async deleteSchemaFailure(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/admin/schema-failures/${id}`)
    return response.data
  }

  async batchDeleteSchemaFailures(ids: string[]): Promise<ApiResponse<{ deleted: number }>> {
    const response = await api.post('/admin/schema-failures/batch-delete', { ids })
    return response.data
  }

  // Support ticket endpoints
  async createSupportTicket(data: CreateSupportTicketRequest): Promise<ApiResponse<SupportTicket>> {
    const response = await api.post('/support/tickets', data)
    return response.data
  }

  async getSupportTickets(): Promise<ApiResponse<SupportTicket[]>> {
    const response = await api.get('/support/tickets')
    return response.data
  }

  async deleteSupportTicket(ticketId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/support/tickets/${ticketId}`)
    return response.data
  }

  async batchDeleteSupportTickets(ticketIds: string[]): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post('/support/tickets/batch-delete', { ticketIds })
    return response.data
  }

  async markTicketAsRead(ticketId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.patch(`/support/tickets/${ticketId}/read`)
    return response.data
  }

  async batchMarkTicketsAsRead(ticketIds: string[]): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post('/support/tickets/batch-read', { ticketIds })
    return response.data
  }

  async archiveTicket(ticketId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.patch(`/support/tickets/${ticketId}/archive`)
    return response.data
  }

  async batchArchiveTickets(ticketIds: string[]): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post('/support/tickets/batch-archive', { ticketIds })
    return response.data
  }

  async unarchiveTicket(ticketId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.patch(`/support/tickets/${ticketId}/unarchive`)
    return response.data
  }

  async batchUnarchiveTickets(ticketIds: string[]): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post('/support/tickets/batch-unarchive', { ticketIds })
    return response.data
  }

  // Demo request endpoints
  async createDemoRequest(data: CreateDemoRequestRequest): Promise<ApiResponse<DemoRequest>> {
    const response = await api.post('/demo-requests', data)
    return response.data
  }

  async getDemoRequests(status?: string): Promise<ApiResponse<DemoRequest[]>> {
    const response = await api.get('/demo-requests', { params: status ? { status } : undefined })
    return response.data
  }

  async updateDemoRequest(id: string, data: UpdateDemoRequestRequest): Promise<ApiResponse<DemoRequest>> {
    const response = await api.patch(`/demo-requests/${id}`, data)
    return response.data
  }

  async markDemoRequestScheduled(
    id: string,
    data: Pick<UpdateDemoRequestRequest, 'calendlyEventUri' | 'calendlyInviteeUri' | 'scheduledAt'>
  ): Promise<ApiResponse<DemoRequest>> {
    const response = await api.post(`/demo-requests/${id}/calendly-scheduled`, data)
    return response.data
  }

  async createAgencyApplication(data: CreateAgencyApplicationRequest): Promise<ApiResponse<AgencyApplication>> {
    const response = await api.post('/agency-applications', data)
    return response.data
  }

  async getAgencyApplications(status?: string): Promise<ApiResponse<AgencyApplication[]>> {
    const response = await api.get('/agency-applications', { params: status ? { status } : undefined })
    return response.data
  }

  async updateAgencyApplication(id: string, data: UpdateAgencyApplicationRequest): Promise<ApiResponse<AgencyApplication>> {
    const response = await api.patch(`/agency-applications/${id}`, data)
    return response.data
  }

  async markAgencyApplicationScheduled(
    id: string,
    data: Pick<UpdateAgencyApplicationRequest, 'calendlyEventUri' | 'calendlyInviteeUri' | 'scheduledAt'>
  ): Promise<ApiResponse<AgencyApplication>> {
    const response = await api.post(`/agency-applications/${id}/calendly-scheduled`, data)
    return response.data
  }

  async createManagedApplication(data: CreateManagedApplicationRequest): Promise<ApiResponse<ManagedApplication>> {
    const response = await api.post('/managed-applications', data)
    return response.data
  }

  async getManagedApplications(status?: string): Promise<ApiResponse<ManagedApplication[]>> {
    const response = await api.get('/managed-applications', { params: status ? { status } : undefined })
    return response.data
  }

  async updateManagedApplication(id: string, data: UpdateManagedApplicationRequest): Promise<ApiResponse<ManagedApplication>> {
    const response = await api.patch(`/managed-applications/${id}`, data)
    return response.data
  }

  async markManagedApplicationScheduled(
    id: string,
    data: Pick<UpdateManagedApplicationRequest, 'calendlyEventUri' | 'calendlyInviteeUri' | 'scheduledAt'>
  ): Promise<ApiResponse<ManagedApplication>> {
    const response = await api.post(`/managed-applications/${id}/calendly-scheduled`, data)
    return response.data
  }

  // Team endpoints
  async listTeams(): Promise<ApiResponse<ListTeamsResponse>> {
    const response = await api.get('/team/list')
    return response.data
  }

  async getCurrentTeam(): Promise<ApiResponse<CurrentTeamResponse>> {
    const response = await api.get('/team/current')
    return response.data
  }

  async switchTeam(teamId: string): Promise<ApiResponse<{ activeTeamId: string; message: string }>> {
    const response = await api.post(`/team/switch/${teamId}`)
    return response.data
  }

  async createTeam(organizationName?: string): Promise<ApiResponse<{ team: any; allTeams: TeamMember[]; message: string }>> {
    const response = await api.post('/team/create', { organizationName })
    return response.data
  }

  async createTeamInvite(role?: 'admin' | 'member'): Promise<ApiResponse<TeamInvite>> {
    const response = await api.post('/team/invite', { role })
    return response.data
  }

  async validateTeamInvite(token: string): Promise<ApiResponse<{ valid: boolean } & TeamInviteValidation>> {
    const response = await api.get(`/team/invite/${token}`)
    return response.data
  }

  async acceptTeamInvite(token: string): Promise<ApiResponse<{ teamId: string; message: string }>> {
    const response = await api.post(`/team/join/${token}`)
    return response.data
  }

  async getTeamMembers(): Promise<ApiResponse<{ members: TeamMember[]; teamId: string; ownerId: string }>> {
    const response = await api.get('/team/members')
    return response.data
  }

  async removeTeamMember(userId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/team/members/${userId}`)
    return response.data
  }

  async updateMemberRole(userId: string, role: 'admin' | 'member'): Promise<ApiResponse<{ message: string }>> {
    const response = await api.patch(`/team/members/${userId}/role`, { role })
    return response.data
  }

  async leaveTeam(): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post('/team/leave')
    return response.data
  }

  async getTeamInvites(): Promise<ApiResponse<TeamInviteDetails[]>> {
    const response = await api.get('/team/invites')
    return response.data
  }

  async deleteTeamInvite(inviteId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/team/invite/${inviteId}`)
    return response.data
  }

  // Organization endpoints
  async listOrganizations(): Promise<ApiResponse<Array<Organization & { completeness: OrganizationCompleteness }>>> {
    const response = await api.get('/organizations')
    return response.data
  }

  async getOrganization(orgId: string): Promise<ApiResponse<GetOrganizationResponse>> {
    const response = await api.get(`/organizations/${orgId}`)
    return response.data
  }

  async createOrganization(data: CreateOrganizationRequest): Promise<ApiResponse<GetOrganizationResponse & { message: string }>> {
    const response = await api.post('/organizations', data)
    return response.data
  }

  async updateOrganization(orgId: string, data: UpdateOrganizationRequest): Promise<ApiResponse<GetOrganizationResponse & { message: string }>> {
    const response = await api.put(`/organizations/${orgId}`, data)
    return response.data
  }

  async deleteOrganization(orgId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/organizations/${orgId}`)
    return response.data
  }

  async setDefaultOrganization(orgId: string): Promise<ApiResponse<{ data: Organization; message: string }>> {
    const response = await api.post(`/organizations/${orgId}/default`)
    return response.data
  }

  async getOrganizationForDomain(domain: string): Promise<ApiResponse<{
    organization: Organization | null
    completeness?: OrganizationCompleteness
    publisherData?: Record<string, any>
    message?: string
  }>> {
    const response = await api.get('/organizations/for-domain', {
      params: { domain }
    })
    return response.data
  }

  // Release notes endpoints
  async getReleaseNotes(): Promise<ApiResponse<ReleaseNote[]>> {
    const response = await api.get('/release-notes')
    return response.data
  }

  // Admin release notes endpoints
  async getAllReleaseNotes(): Promise<ApiResponse<ReleaseNote[]>> {
    const response = await api.get('/release-notes/admin/all')
    return response.data
  }

  async createReleaseNote(data: CreateReleaseNoteRequest): Promise<ApiResponse<ReleaseNote>> {
    const response = await api.post('/release-notes/admin', data)
    return response.data
  }

  async updateReleaseNote(noteId: string, data: UpdateReleaseNoteRequest): Promise<ApiResponse<ReleaseNote>> {
    const response = await api.put(`/release-notes/admin/${noteId}`, data)
    return response.data
  }

  async deleteReleaseNote(noteId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/release-notes/admin/${noteId}`)
    return response.data
  }

  // Blog/Resources endpoints (public)
  async getPublishedBlogPosts(params?: {
    page?: number
    limit?: number
    category?: string
    tag?: string
  }): Promise<ApiResponse<BlogPostsResponse>> {
    const response = await api.get('/resources', { params })
    return response.data
  }

  async getBlogPostBySlug(slug: string): Promise<ApiResponse<BlogPost>> {
    const response = await api.get(`/resources/${slug}`)
    return response.data
  }

  async getBlogCategories(): Promise<ApiResponse<BlogCategoryCount[]>> {
    const response = await api.get('/resources/categories')
    return response.data
  }

  /*
    /learn, the public video library (no auth). Typed as the envelope the
    server actually sends: ApiResponse<T> intersects `data?: any` with T,
    which leaves `.data` untyped, and a catalogue page wants the shape.
  */
  async getLearnListing(): Promise<{ success: boolean; data: LearnListing; error?: string }> {
    const response = await api.get('/learn')
    return response.data
  }

  async getLearnVideo(slug: string): Promise<{ success: boolean; data: LearnVideoDetail; error?: string }> {
    const response = await api.get(`/learn/videos/${encodeURIComponent(slug)}`)
    return response.data
  }

  async getLearnCategory(slug: string, page: number): Promise<{ success: boolean; data: LearnCategoryDetail; error?: string }> {
    const response = await api.get(`/learn/categories/${encodeURIComponent(slug)}`, { params: { page } })
    return response.data
  }

  /** Admin: paste a YouTube link, get the lesson's starting facts. */
  async lookupYouTubeVideo(url: string): Promise<{ success: boolean; data: YouTubeLookupResult & { watchUrl: string }; error?: string }> {
    const response = await api.post('/admin/education/youtube-lookup', { url })
    return response.data
  }

  // Blog/Resources admin endpoints
  async getAdminBlogPosts(params?: {
    page?: number
    limit?: number
    status?: string
    category?: string
    search?: string
  }): Promise<ApiResponse<BlogPostsResponse>> {
    const response = await api.get('/resources/admin/all', { params })
    return response.data
  }

  async getAdminBlogPost(id: string): Promise<ApiResponse<BlogPost>> {
    const response = await api.get(`/resources/admin/${id}`)
    return response.data
  }

  async createBlogPost(data: CreateBlogPostRequest): Promise<ApiResponse<BlogPost>> {
    const response = await api.post('/resources/admin', data)
    return response.data
  }

  async updateBlogPost(id: string, data: UpdateBlogPostRequest): Promise<ApiResponse<BlogPost>> {
    const response = await api.put(`/resources/admin/${id}`, data)
    return response.data
  }

  async deleteBlogPost(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/resources/admin/${id}`)
    return response.data
  }

  async publishBlogPost(id: string): Promise<ApiResponse<BlogPost>> {
    const response = await api.post(`/resources/admin/${id}/publish`)
    return response.data
  }

  async unpublishBlogPost(id: string): Promise<ApiResponse<BlogPost>> {
    const response = await api.post(`/resources/admin/${id}/unpublish`)
    return response.data
  }

  async regenerateBlogAlternateFiles(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.post(`/resources/admin/${id}/regenerate-alternate-files`)
    return response.data
  }

  async uploadBlogImage(data: { data: string; fileName: string; contentType: string }): Promise<ApiResponse<{ url: string }>> {
    const response = await api.post('/resources/admin/upload-image', data)
    return response.data
  }

  // Tutorial video endpoints
  async getTutorialVideoPlacements(keys: string[]): Promise<ApiResponse<TutorialVideoPlacement[]>> {
    const response = await api.get('/tutorial-videos/placements', {
      params: { keys: keys.join(',') }
    })
    return response.data
  }

  async trackTutorialVideoEvent(videoId: string, data: {
    placementKey: string
    eventType: TutorialVideoEventType
    viewerKey: string
    currentTimeSeconds?: number | null
    durationSeconds?: number | null
    metadata?: Record<string, unknown>
  }): Promise<ApiResponse<{ tracked: boolean }>> {
    const response = await api.post(`/tutorial-videos/${videoId}/events`, data)
    return response.data
  }

  async getAdminTutorialVideos(): Promise<ApiResponse<AdminTutorialVideo[]>> {
    const response = await api.get('/admin/tutorial-videos')
    return response.data
  }

  async createAdminTutorialVideo(data: {
    title: string
    description?: string | null
    durationSeconds?: number
    status?: 'draft' | 'active' | 'archived'
    placementKey?: string
    pagePathPattern?: string | null
  }): Promise<ApiResponse<AdminTutorialVideo>> {
    const response = await api.post('/admin/tutorial-videos', data)
    return response.data
  }

  async updateAdminTutorialVideo(id: string, data: {
    title?: string
    description?: string | null
    durationSeconds?: number
    status?: 'draft' | 'active' | 'archived'
    placementKey?: string
    pagePathPattern?: string | null
    placementActive?: boolean
    titleOverride?: string | null
    descriptionOverride?: string | null
  }): Promise<ApiResponse<AdminTutorialVideo>> {
    const response = await api.patch(`/admin/tutorial-videos/${id}`, data)
    return response.data
  }

  async createTutorialVideoSignedUpload(id: string, data: {
    assetType: 'video' | 'thumbnail' | 'captions'
    fileName: string
    contentType: string
    fileSize: number
  }): Promise<ApiResponse<TutorialVideoSignedUpload>> {
    const response = await api.post(`/admin/tutorial-videos/${id}/assets`, data)
    return response.data
  }

  async confirmTutorialVideoAsset(id: string, data: {
    assetType: 'video' | 'thumbnail' | 'captions'
    path: string
  }): Promise<ApiResponse<AdminTutorialVideo>> {
    const response = await api.post(`/admin/tutorial-videos/${id}/assets/confirm`, data)
    return response.data
  }

  async setTutorialVideoAssetUrl(id: string, data: {
    assetType: 'video' | 'thumbnail' | 'captions'
    url: string
  }): Promise<ApiResponse<AdminTutorialVideo>> {
    const response = await api.post(`/admin/tutorial-videos/${id}/assets/url`, data)
    return response.data
  }

  async getAdminTutorialVideoAnalytics(id: string): Promise<ApiResponse<TutorialVideoAnalytics>> {
    const response = await api.get(`/admin/tutorial-videos/${id}/analytics`)
    return response.data
  }

  // Learn content admin endpoints
  async getAdminEducationOverview(): Promise<ApiResponse<EducationAdminOverview>> {
    const response = await api.get('/admin/education/overview')
    return response.data
  }

  async createEducationCategory(data: Partial<EducationCategory> & { name: string }): Promise<ApiResponse<EducationCategory>> {
    const response = await api.post('/admin/education/categories', data)
    return response.data
  }

  async updateEducationCategory(id: string, data: Partial<EducationCategory>): Promise<ApiResponse<EducationCategory>> {
    const response = await api.patch(`/admin/education/categories/${id}`, data)
    return response.data
  }

  async deleteEducationCategory(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/admin/education/categories/${id}`)
    return response.data
  }

  async createEducationTag(data: Partial<EducationTag> & { name: string }): Promise<ApiResponse<EducationTag>> {
    const response = await api.post('/admin/education/tags', data)
    return response.data
  }

  async updateEducationTag(id: string, data: Partial<EducationTag>): Promise<ApiResponse<EducationTag>> {
    const response = await api.patch(`/admin/education/tags/${id}`, data)
    return response.data
  }

  async deleteEducationTag(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/admin/education/tags/${id}`)
    return response.data
  }

  async createEducationItem(data: Partial<EducationItem> & { title: string; tagIds?: string[] }): Promise<ApiResponse<EducationItem>> {
    const response = await api.post('/admin/education/items', data)
    return response.data
  }

  async updateEducationItem(id: string, data: Partial<EducationItem> & { tagIds?: string[] }): Promise<ApiResponse<EducationItem>> {
    const response = await api.patch(`/admin/education/items/${id}`, data)
    return response.data
  }

  async archiveEducationItem(id: string): Promise<ApiResponse<EducationItem>> {
    const response = await api.delete(`/admin/education/items/${id}`)
    return response.data
  }

  async permanentlyDeleteEducationItem(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/admin/education/items/${id}/permanent`)
    return response.data
  }

  async publishEducationItem(id: string): Promise<ApiResponse<EducationItem>> {
    const response = await api.post(`/admin/education/items/${id}/publish`)
    return response.data
  }

  async unpublishEducationItem(id: string): Promise<ApiResponse<EducationItem>> {
    const response = await api.post(`/admin/education/items/${id}/unpublish`)
    return response.data
  }

  async createEducationSeries(data: Partial<EducationSeries> & { title: string }): Promise<ApiResponse<EducationSeries>> {
    const response = await api.post('/admin/education/series', data)
    return response.data
  }

  async updateEducationSeries(id: string, data: Partial<EducationSeries>): Promise<ApiResponse<EducationSeries>> {
    const response = await api.patch(`/admin/education/series/${id}`, data)
    return response.data
  }

  async archiveEducationSeries(id: string): Promise<ApiResponse<EducationSeries>> {
    const response = await api.delete(`/admin/education/series/${id}`)
    return response.data
  }

  async publishEducationSeries(id: string): Promise<ApiResponse<EducationSeries>> {
    const response = await api.post(`/admin/education/series/${id}/publish`)
    return response.data
  }

  async unpublishEducationSeries(id: string): Promise<ApiResponse<EducationSeries>> {
    const response = await api.post(`/admin/education/series/${id}/unpublish`)
    return response.data
  }

  async setEducationSeriesItems(id: string, ids: string[]): Promise<ApiResponse<EducationSeries[]>> {
    const response = await api.patch(`/admin/education/series/${id}/items`, { ids })
    return response.data
  }

  // Advanced Analytics endpoints
  async getPowerUsersAnalytics(period: '7d' | '30d' = '30d'): Promise<ApiResponse<{
    period: string
    users: Array<{
      userId: string
      email: string
      firstName: string | null
      lastName: string | null
      powerScore: number
      activeDays: number
      totalLogins: number
      schemasGenerated: number
      revenueInCents: number
      lastActiveAt: string
      trend: 'up' | 'stable' | 'down'
    }>
    totalCount: number
  }>> {
    const response = await api.get(`/admin/analytics/power-users?period=${period}`)
    return response.data
  }

  async getSchemaQualityAnalytics(period: '7d' | '30d' = '30d'): Promise<ApiResponse<{
    period: string
    averageQualityScore: number
    refinementRate: number
    averageComplexity: number
    successRate: number
    totalSchemas: number
    qualityTrend: 'improving' | 'stable' | 'declining'
    schemasByType: Array<{ type: string; count: number; avgScore: number }>
  }>> {
    const response = await api.get(`/admin/analytics/schema-quality?period=${period}`)
    return response.data
  }

  async getConversionAnalytics(): Promise<ApiResponse<{
    conversionRate: number
    totalSignups: number
    totalConversions: number
    averageTimeToConversion: number
    conversionFunnel: {
      signups: number
      firstSchema: number
      firstPurchase: number
      dropoffAfterSignup: number
      dropoffAfterFirstSchema: number
    }
    recentTrend: {
      last7Days: number
      last30Days: number
      trendDirection: 'up' | 'stable' | 'down'
    }
  }>> {
    const response = await api.get('/admin/analytics/conversions')
    return response.data
  }

  async getPurchaseAnalytics(): Promise<ApiResponse<{
    purchaseTransactions: Array<{
      purchaseId: string
      userEmail: string
      userName: string
      creditPackName: string
      credits: number
      amountPaid: number
      purchaseDate: string
      userLTV: number
      daysToFirstPurchase: number
    }>
    recentPurchasers: Array<{
      purchaseId: string
      userEmail: string
      userName: string
      creditPackName: string
      credits: number
      amountPaid: number
      purchaseDate: string
      userLTV: number
      daysToFirstPurchase: number
    }>
    metrics: {
      totalPayingCustomers: number
      totalPurchases: number
      totalRevenue: number
      arppu: number
      repeatPurchaseRate: number
      repeatCustomers: number
      avgTimeToFirstPurchase: number
      avgCreditUtilization: number
    }
    creditUtilization: Array<{
      userId: string
      creditsPurchased: number
      creditsUsed: number
      utilizationRate: number
    }>
  }>> {
    const response = await api.get('/admin/analytics/purchases')
    return response.data
  }

  async getSubscriptionAnalytics(): Promise<ApiResponse<{
    currentMRR: number
    activeSubscriberCount: number
    monthlySubscribers: number
    annualSubscribers: number
    mrrGrowthRate: number
    subscriberGrowthRate: number
    churnRate: number
    churnedMRR: number
    churnedSubscriberCount: number
    newMRR: number
    newSubscriberCount: number
    netMRRMovement: number
    arpu: number
    arr: number
    paidInvoiceRevenueThisMonth: number
    paidInvoiceRevenueTotal: number
    planBreakdown: {
      monthly: { count: number; mrr: number; percentage: number }
      annual: { count: number; mrr: number; percentage: number }
    }
    tierBreakdown: Record<string, {
      key: string
      label: string
      aggregateTier: string
      count: number
      mrr: number
      percentage: number
    }>
    revenueByTier: Record<string, number>
    activeSubscribersByTier: Record<string, number>
    mrrByTier: Record<string, number>
    recentPaidInvoices: Array<{
      stripeInvoiceId: string | null
      userId: string | null
      subscriptionId: string | null
      amount: number
      currency: string
      createdAt: string
    }>
    trends: Array<{
      month: string
      mrr: number
      subscriberCount: number
      churnRate: number
    }>
    activeSubscribers: Array<{
      userId: string
      email: string
      firstName: string | null
      lastName: string | null
      plan: 'month' | 'year'
      planName: string
      tier: string
      tierLabel: string
      mrr: number
      status: 'active' | 'trialing' | 'past_due'
      startedAt: string
      currentPeriodEnd: string
    }>
    churnedSubscribers: Array<{
      userId: string
      email: string
      firstName: string | null
      lastName: string | null
      plan: 'month' | 'year'
      planName: string
      tier: string
      tierLabel: string
      wasMRR: number
      canceledAt: string | null
      endedAt: string | null
      mrrLostAt: string
      churnTiming: 'ended' | 'canceled'
      stripeCustomerId: string | null
      stripeSubscriptionId: string | null
      duration: number
    }>
  }>> {
    const response = await api.get('/admin/analytics/subscriptions')
    return response.data
  }

  // AI Packet Export - Admin Endpoints

  /**
   * Export an AI-readable packet (product-truth, daily-data, or health-risk)
   */
  async exportPacket(packetType: 'product-truth' | 'daily-data' | 'health-risk', format: 'md' | 'json' = 'md'): Promise<Blob> {
    const response = await api.get(`/admin/export/${packetType}`, {
      params: { format },
      responseType: 'blob'
    })
    return response.data
  }

  // Private Beta Management - Admin Endpoints

  async getBetaRequests(filters?: {
    featureId?: string
    isPaying?: boolean
    granted?: boolean
    limit?: number
    offset?: number
  }): Promise<ApiResponse<{
    requests: Array<any>
    total: number
    limit: number
    offset: number
  }>> {
    const params = new URLSearchParams()
    if (filters?.featureId) params.append('featureId', filters.featureId)
    if (filters?.isPaying !== undefined) params.append('isPaying', String(filters.isPaying))
    if (filters?.granted !== undefined) params.append('granted', String(filters.granted))
    if (filters?.limit) params.append('limit', String(filters.limit))
    if (filters?.offset) params.append('offset', String(filters.offset))

    const response = await api.get(`/admin/beta-requests?${params.toString()}`)
    return response.data
  }

  async grantBetaAccess(requestId: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/admin/beta-requests/${requestId}/grant`)
    return response.data
  }

  async revokeBetaAccess(userId: string, featureId: string): Promise<ApiResponse<any>> {
    const response = await api.post('/admin/beta-access/revoke', { userId, featureId })
    return response.data
  }

  async getFeatures(): Promise<ApiResponse<any>> {
    const response = await api.get('/admin/features')
    return response.data
  }

  async updateFeatureStatus(featureId: string, status: string): Promise<ApiResponse<any>> {
    const response = await api.patch(`/admin/features/${featureId}/status`, { status })
    return response.data
  }

  // Private Beta Management - User Endpoints

  async requestFeatureBetaAccess(featureId: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/features/${featureId}/request-beta`)
    return response.data
  }

  async checkFeatureAccess(featureSlug: string): Promise<ApiResponse<{
    hasAccess: boolean
    feature: any
    reason: string
    hasPendingRequest?: boolean
    requestedAt?: string
  }>> {
    const response = await api.get(`/features/${featureSlug}/access`)
    return response.data
  }

  async getAllFeatures(): Promise<ApiResponse<{ features: Array<any> }>> {
    const response = await api.get('/features')
    return response.data
  }

  // Notifications

  async getUserNotifications(unreadOnly?: boolean): Promise<ApiResponse<{ notifications: Array<any> }>> {
    const params = new URLSearchParams()
    if (unreadOnly) params.append('unreadOnly', 'true')
    const response = await api.get(`/features/notifications?${params.toString()}`)
    return response.data
  }

  async markNotificationRead(notificationId: string): Promise<ApiResponse<any>> {
    const response = await api.patch(`/features/notifications/${notificationId}/read`)
    return response.data
  }

  async markAllNotificationsRead(): Promise<ApiResponse<any>> {
    const response = await api.patch('/features/notifications/read-all')
    return response.data
  }

  // Version detection - fetches app version from health endpoint
  async getVersion(): Promise<{ version: string; buildTime: string }> {
    const response = await axios.get(`${API_URL}/health`)
    return {
      version: response.data.version,
      buildTime: response.data.buildTime
    }
  }

  // =============================================================================
  // ROADMAP ENDPOINTS
  // =============================================================================

  // User endpoints
  async getRoadmapCategories(): Promise<ApiResponse<RoadmapCategory[]>> {
    const response = await api.get('/roadmap/categories')
    return response.data
  }

  async getRoadmapFeatures(): Promise<ApiResponse<RoadmapFeaturesResponse>> {
    const response = await api.get('/roadmap/features')
    return response.data
  }

  async submitRoadmapIdea(data: CreateRoadmapIdeaRequest): Promise<ApiResponse<RoadmapFeature>> {
    const response = await api.post('/roadmap/ideas', data)
    return response.data
  }

  async voteForFeature(featureId: string): Promise<ApiResponse<{ voteCount: number }>> {
    const response = await api.post(`/roadmap/features/${featureId}/vote`)
    return response.data
  }

  async unvoteForFeature(featureId: string): Promise<ApiResponse<{ voteCount: number }>> {
    const response = await api.delete(`/roadmap/features/${featureId}/vote`)
    return response.data
  }

  // Admin endpoints
  async getAdminRoadmapStats(): Promise<ApiResponse<AdminRoadmapStats>> {
    const response = await api.get('/roadmap/admin/stats')
    return response.data
  }

  async getAdminRoadmapFeatures(): Promise<ApiResponse<RoadmapFeature[]>> {
    const response = await api.get('/roadmap/admin/features')
    return response.data
  }

  async getPendingRoadmapFeatures(): Promise<ApiResponse<RoadmapFeature[]>> {
    const response = await api.get('/roadmap/admin/pending')
    return response.data
  }

  async getFeatureWithVoters(featureId: string): Promise<ApiResponse<RoadmapFeatureWithVoters>> {
    const response = await api.get(`/roadmap/admin/features/${featureId}`)
    return response.data
  }

  async createRoadmapFeature(data: CreateRoadmapFeatureRequest): Promise<ApiResponse<RoadmapFeature>> {
    const response = await api.post('/roadmap/admin/features', data)
    return response.data
  }

  async updateRoadmapFeature(featureId: string, data: UpdateRoadmapFeatureRequest): Promise<ApiResponse<RoadmapFeature>> {
    const response = await api.put(`/roadmap/admin/features/${featureId}`, data)
    return response.data
  }

  async deleteRoadmapFeature(featureId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/roadmap/admin/features/${featureId}`)
    return response.data
  }

  async approveRoadmapFeature(featureId: string): Promise<ApiResponse<RoadmapFeature>> {
    const response = await api.post(`/roadmap/admin/features/${featureId}/approve`)
    return response.data
  }

  async rejectRoadmapFeature(featureId: string): Promise<ApiResponse<void>> {
    const response = await api.post(`/roadmap/admin/features/${featureId}/reject`)
    return response.data
  }

  async reorderRoadmapFeatures(data: ReorderRoadmapFeaturesRequest): Promise<ApiResponse<void>> {
    const response = await api.put('/roadmap/admin/reorder', data)
    return response.data
  }

  // Category admin endpoints
  async createRoadmapCategory(data: CreateRoadmapCategoryRequest): Promise<ApiResponse<RoadmapCategory>> {
    const response = await api.post('/roadmap/admin/categories', data)
    return response.data
  }

  async updateRoadmapCategory(categoryId: string, data: UpdateRoadmapCategoryRequest): Promise<ApiResponse<RoadmapCategory>> {
    const response = await api.put(`/roadmap/admin/categories/${categoryId}`, data)
    return response.data
  }

  async deleteRoadmapCategory(categoryId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/roadmap/admin/categories/${categoryId}`)
    return response.data
  }

  // =============================================================================
  // SUBSCRIPTION ENDPOINTS
  // =============================================================================

  /**
   * Get all active subscription plans
   */
  async getSubscriptionPlans(): Promise<ApiResponse<SubscriptionPlan[]>> {
    const response = await api.get('/subscription/plans')
    return response.data
  }

  /**
   * Create subscription checkout session
   */
  async createSubscriptionCheckout(planId: string, returnUrl?: string): Promise<ApiResponse<CreateSubscriptionCheckoutResponse>> {
    const response = await api.post('/subscription/checkout', { planId, returnUrl })
    return response.data
  }

  /**
   * Verify checkout session completion
   */
  async verifySubscriptionCheckout(sessionId: string): Promise<ApiResponse<{ subscription: UserSubscription }>> {
    const response = await api.post('/subscription/checkout/verify', { sessionId })
    return response.data
  }

  /**
   * Create subscription intent for custom payment form (CardElement)
   */
  async createSubscriptionIntent(input: string | {
    planId?: string | null
    tier?: string | null
    billingInterval?: 'month' | 'year'
  }): Promise<ApiResponse<{
    clientSecret: string
    /**
     * Which Stripe call confirms this secret. 'setup' means a 7-day trial,
     * which has nothing to charge and so produces a SetupIntent instead of a
     * PaymentIntent. Optional because a server that predates the trial does not
     * send it; callers default to 'payment'.
     */
    mode?: 'setup' | 'payment'
    /**
     * Null on the trial path. A trial creates no subscription until the card is
     * confirmed, so there is nothing to name yet.
     */
    subscriptionId: string | null
    /**
     * Set on the trial path only. Hand this back to /confirm and the server
     * creates the subscription from the card that was just saved.
     */
    setupIntentId?: string | null
    customerId: string
  }>> {
    const payload = typeof input === 'string' ? { planId: input } : input
    const response = await api.post('/subscription/create-intent', payload)
    return response.data
  }

  /**
   * Records that this person saw the 7-day trial offer and reports whether
   * they may still take one. Safe to call on every plans-panel view: the stamp
   * is idempotent and keeps the original timestamp.
   */
  async stampTrialEligibility(): Promise<ApiResponse<{ eligible: boolean }>> {
    const response = await api.post('/subscription/trial-eligibility')
    return response.data
  }

  /**
   * Converts a trial to a paid subscription immediately, from the page cap.
   *
   * A declined card returns ok: false with a message rather than throwing, so
   * the caller must read data.ok and show data.message.
   */
  async endTrialNow(confirmation: { teamId: string; subscriptionId: string; stripePriceId: string }): Promise<{ success: boolean; data?: { ok: boolean; status: string | null; message: string | null }; error?: string }> {
    const response = await api.post('/subscription/end-trial', confirmation)
    return response.data
  }

  /**
   * Confirm subscription payment was successful
   */
  /**
   * Finishes a checkout.
   *
   * A paid signup passes the subscription it just paid for. A trial passes the
   * SetupIntent it just confirmed, and the subscription is created server-side
   * from that card: a trial never exists without one.
   */
  async confirmSubscriptionPayment(
    ref: string | { subscriptionId?: string | null; setupIntentId?: string | null }
  ): Promise<ApiResponse<{ subscription: UserSubscription }>> {
    const payload = typeof ref === 'string' ? { subscriptionId: ref } : ref
    const response = await api.post('/subscription/confirm', payload)
    return response.data
  }

  /**
   * Get user's current subscription
   */
  async getCurrentSubscription(): Promise<ApiResponse<UserSubscription | null>> {
    const response = await api.get('/subscription/current')
    return response.data
  }

  /**
   * Get full billing overview
   */
  async getBillingOverview(): Promise<ApiResponse<BillingOverview>> {
    const response = await api.get('/subscription/billing')
    return response.data
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(immediately?: boolean): Promise<ApiResponse<UserSubscription>> {
    const response = await api.post('/subscription/cancel', { immediately })
    return response.data
  }

  /*
    The trial cancellation flow. Every call is authenticated and scoped to the
    caller's own subscription; the server decides which offer applies and this
    client never names one it was not given.
  */
  async startTrialCancellation(reason: string): Promise<ApiResponse<{ id: string; offer: string }>> {
    const response = await api.post('/trial-cancellation', { reason })
    return response.data
  }

  async answerTrialCancellation(id: string, patch: Record<string, unknown>): Promise<ApiResponse<unknown>> {
    const response = await api.patch(`/trial-cancellation/${id}`, patch)
    return response.data
  }

  async acceptTrialExtension(feedbackId: string): Promise<ApiResponse<{ trialEnd: string | null }>> {
    const response = await api.post('/trial-cancellation/extend', { feedbackId })
    return response.data
  }

  async acceptTrialDowngrade(feedbackId: string): Promise<ApiResponse<{ planId: string }>> {
    const response = await api.post('/trial-cancellation/downgrade', { feedbackId })
    return response.data
  }

  async getTrialCancellations(filters: { needsOutreach?: boolean } = {}): Promise<ApiResponse<unknown[]>> {
    const response = await api.get('/trial-cancellation/admin/list', {
      params: filters.needsOutreach ? { needsOutreach: 'true' } : {},
    })
    return response.data
  }

  async updateTrialCancellationOutreach(
    id: string,
    updates: { outreachStatus?: string; adminNotes?: string | null }
  ): Promise<ApiResponse<unknown>> {
    const response = await api.patch(`/trial-cancellation/admin/${id}`, updates)
    return response.data
  }

  /**
   * Reactivate subscription (undo cancel at period end)
   */
  async reactivateSubscription(): Promise<ApiResponse<UserSubscription>> {
    const response = await api.post('/subscription/reactivate')
    return response.data
  }

  /**
   * Upgrade monthly subscription to annual
   */
  async upgradeToAnnual(): Promise<ApiResponse<{ subscription: UserSubscription }>> {
    const response = await api.post('/subscription/upgrade-to-annual')
    return response.data
  }

  /**
   * Pause subscription for one billing period
   */
  async pauseSubscription(): Promise<ApiResponse<UserSubscription>> {
    const response = await api.post('/subscription/pause')
    return response.data
  }

  /**
   * Resume a paused subscription early
   */
  async resumePausedSubscription(): Promise<ApiResponse<UserSubscription>> {
    const response = await api.post('/subscription/resume-pause')
    return response.data
  }

  /**
   * Get available subscription tiers with pricing
   */
  async getSubscriptionTiers(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/subscription/tiers')
    return response.data
  }

  /**
   * Get current team usage and plan limits
   */
  async getSubscriptionUsage(): Promise<ApiResponse<any>> {
    const response = await api.get('/subscription/usage')
    return response.data
  }

  /**
   * Change subscription to a different tier plan
   */
  async previewSubscriptionTierChange(input: string | {
    planId?: string
    tier?: string
    billingInterval?: 'month' | 'year'
  }): Promise<ApiResponse<any>> {
    const payload = typeof input === 'string' ? { planId: input } : input
    const response = await api.post('/subscription/change/preview', payload)
    return response.data
  }

  async changeSubscriptionTier(input: string | {
    planId?: string
    tier?: string
    billingInterval?: 'month' | 'year'
    prorationDate?: number
    confirmLegacyPriceLoss?: boolean
  }): Promise<ApiResponse<any>> {
    const payload = typeof input === 'string' ? { planId: input } : input
    const response = await api.post('/subscription/change', payload)
    return response.data
  }

  async cancelPendingSubscriptionTierChange(): Promise<ApiResponse<any>> {
    const response = await api.post('/subscription/change/cancel-pending')
    return response.data
  }

  /**
   * Create SetupIntent for updating payment method
   */
  async createPaymentMethodSetup(): Promise<ApiResponse<PaymentMethodSetupResponse>> {
    const response = await api.post('/subscription/payment-method/setup')
    return response.data
  }

  /**
   * Update default payment method
   */
  async updatePaymentMethod(paymentMethodId: string): Promise<ApiResponse<void>> {
    const response = await api.put('/subscription/payment-method', { paymentMethodId })
    return response.data
  }

  /**
   * Download invoice PDF (generated on-demand)
   */
  async downloadInvoicePdf(invoiceId: string): Promise<Blob> {
    const response = await api.get(`/subscription/invoices/${invoiceId}/pdf`, {
      responseType: 'blob'
    })
    return response.data
  }

  /**
   * Get user's billing information for invoices
   */
  async getBillingInfo(): Promise<ApiResponse<UserBillingInfo | null>> {
    const response = await api.get('/subscription/billing-info')
    return response.data
  }

  /**
   * Update user's billing information for invoices
   */
  async updateBillingInfo(data: UpdateBillingInfoRequest): Promise<ApiResponse<UserBillingInfo>> {
    const response = await api.put('/subscription/billing-info', data)
    return response.data
  }

  /**
   * Get all invoices for a user (both subscription and credit pack)
   */
  async getAllInvoices(limit?: number): Promise<ApiResponse<Invoice[]>> {
    const params = limit ? `?limit=${limit}` : ''
    const response = await api.get(`/subscription/all-invoices${params}`)
    return response.data
  }

  /**
   * Download any invoice PDF (subscription or credit pack)
   */
  async downloadAnyInvoicePdf(invoiceId: string): Promise<Blob> {
    const response = await api.get(`/subscription/all-invoices/${invoiceId}/pdf`, {
      responseType: 'blob'
    })
    return response.data
  }

  // =================================================================
  // SuperSchema Score
  // =================================================================

  /**
   * Get all AI Visibility sites for the current user with their latest scans
   */
  async getAIVisibilityUserSites(): Promise<ApiResponse<{
    sites: Array<{
      site: {
        id: string;
        domain: string;
        created_at: string;
        updated_at: string;
      };
      latestScan: {
        id: string;
        status: string;
        overall_score?: number;
        access_score?: number;
        content_score?: number;
        structure_score?: number;
        schema_score?: number;
        pages_analyzed?: number;
        pages_total?: number;
        completed_at?: string;
        created_at: string;
      } | null;
    }>;
    count: number;
    maxSites: number;
    isPro: boolean;
  }>> {
    const response = await api.get('/ai-visibility/sites')
    return response.data
  }

  /**
   * Start a new AI Visibility scan for a domain
   */
  async startAIVisibilityScan(domain: string, options?: { forceRescan?: boolean; quickScan?: boolean; isWelcomeScan?: boolean }): Promise<ApiResponse<{
    scan: {
      id: string;
      status: string;
      domain: string;
      created_at: string;
    };
  }>> {
    const response = await api.post('/ai-visibility/scan', {
      domain,
      forceRescan: options?.forceRescan,
      quickScan: options?.quickScan,
      isWelcomeScan: options?.isWelcomeScan
    })
    return response.data
  }

  /**
   * Get AI Visibility scan status and results
   */
  async getAIVisibilityScan(scanId: string): Promise<ApiResponse<{
    scan: any;
    findings: any[];
    pages: any[];
    domainRecord?: UnifiedDomain | null;
  }>> {
    const response = await api.get(`/ai-visibility/scan/${scanId}`)
    return response.data
  }

  /**
   * Get AI Visibility scan progress (for polling during active scan)
   */
  async getAIVisibilityScanProgress(scanId: string): Promise<ApiResponse<{
    progress: {
      status: string;
      currentStep: string;
      completedSteps: string[];
      message: string;
      pagesAnalyzed: number;
      totalPages: number | null;
      currentPageUrl?: string;
      phase?: 'discovery' | 'crawling' | 'analyzing' | 'scoring';
      phaseNumber?: number;
      estimatedSecondsRemaining?: number;
      avgPageTimeMs?: number;
      backgroundMode?: boolean;
    };
  }>> {
    const response = await api.get(`/ai-visibility/scan/${scanId}/progress`)
    return response.data
  }

  /**
   * Enable background mode for an AI Visibility scan
   * User will be notified when the scan completes
   */
  async enableAIVisibilityBackgroundMode(scanId: string): Promise<ApiResponse<{
    message: string;
  }>> {
    const response = await api.post(`/ai-visibility/scan/${scanId}/background`)
    return response.data
  }

  /**
   * Cancel a running AI Visibility scan
   */
  async cancelAIVisibilityScan(scanId: string): Promise<ApiResponse<{
    message: string;
  }>> {
    const response = await api.post(`/ai-visibility/scan/${scanId}/cancel`)
    return response.data
  }

  /**
   * Get latest AI Visibility scan for a domain
   */
  async getLatestAIVisibilityScan(domain: string): Promise<ApiResponse<{
    scan: any;
    findings?: any[];
    pages?: any[];
  }>> {
    const response = await api.get(`/ai-visibility/sites/${encodeURIComponent(domain)}/latest`)
    return response.data
  }

  /**
   * Get AI Visibility scan history for a domain
   */
  async getAIVisibilityScanHistory(domain: string, limit?: number): Promise<ApiResponse<{
    scans: any[];
  }>> {
    const params = limit ? `?limit=${limit}` : ''
    const response = await api.get(`/ai-visibility/sites/${encodeURIComponent(domain)}/history${params}`)
    return response.data
  }

  /**
   * Get AI Visibility scan retention stats for a site
   * Returns count of stored scans and the limit based on subscription tier
   */
  async getAIVisibilityScanStats(siteId: string): Promise<ApiResponse<{
    scanCount: number;
    scanLimit: number;
    isPro: boolean;
  }>> {
    const response = await api.get(`/ai-visibility/sites/${siteId}/stats`)
    return response.data
  }

  /**
   * Delete an AI Visibility site and all its scan history
   */
  async deleteAIVisibilitySite(siteId: string): Promise<ApiResponse<{
    message: string;
  }>> {
    const response = await api.delete(`/ai-visibility/sites/${siteId}`)
    return response.data
  }

  /**
   * Get AI Visibility scan usage statistics for current billing period
   * Returns usage info for both Pro and Free users
   */
  async getAIVisibilityScanUsage(): Promise<ApiResponse<{
    isPro: boolean;
    usage: {
      // Pro fields
      fullScansUsed?: number;
      fullScansLimit?: number;
      pageRescansUsed?: number;
      pageRescansLimit?: number;
      periodStart?: string;
      periodEnd?: string;
      // Free fields (monthly limits)
      monthlyScansUsed?: number;
      monthlyScansLimit?: number;
      pagesPerScan?: number;
      maxSites?: number;
    } | null;
  }>> {
    const response = await api.get('/ai-visibility/usage')
    return response.data
  }

  /**
   * Rescan a single page and recalculate domain scores
   * Pro feature only - counts against daily page rescan limit
   */
  async rescanAIVisibilityPage(pageId: string): Promise<ApiResponse<{
    page: any;
    scores: {
      overall: number;
      access: number;
      content: number;
      structure: number;
      schema: number;
      alternate: number;
    };
    findings?: any[];
    usage: {
      pageRescansUsed: number;
      pageRescansLimit: number;
    };
    rescan?: {
      rescanResult: 'changed' | 'unchanged' | 'fetch_issue';
      fetchedAt: string;
      finalUrl: string;
      pageTitle?: string | null;
      diagnosticSummary?: string;
      scoreDelta: {
        overall: number;
        before: number;
        after: number;
      };
      categoryDeltas?: Record<string, number>;
      signalDeltas?: Array<{
        label: string;
        before: boolean | number | string | null;
        after: boolean | number | string | null;
        changed: boolean;
      }>;
      diagnostics?: {
        h1Count: number;
        wordCount: number;
        hasSchema: boolean;
        hasAlternateFiles: boolean;
        summary: string;
      };
    };
  }>> {
    const response = await api.post(`/ai-visibility/pages/${pageId}/rescan`)
    return response.data
  }

  /**
   * Get detailed page data by short ID for the page detail view
   */
  async getAIVisibilityPageDetail(shortId: string): Promise<ApiResponse<{
    page: any;
    scan: any;
    site: { domain: string; id: string };
    findings: any[];
    schemaGenerations: any[];
    alternateFiles: any[];
  }>> {
    const response = await api.get(`/ai-visibility/pages/${shortId}`)
    return response.data
  }

  async getPageDeletionPreview(shortId: string): Promise<ApiResponse<{
    scanPages: number;
    findings: number;
    schemaGenerations: number;
    alternateFiles: number;
    discoveredUrls: number;
    totalRecords: number;
  }>> {
    const response = await api.get(`/ai-visibility/pages/${shortId}/deletion-preview`)
    return response.data
  }

  async deleteAIVisibilityPage(shortId: string): Promise<ApiResponse<{
    deletedPages: number;
    deletedSchemaGenerations: number;
    deletedAlternateFiles: number;
    deletedDiscoveredUrls: number;
    deletedEmptyScans: number;
    domain: string;
  }>> {
    const response = await api.delete(`/ai-visibility/pages/${shortId}`)
    return response.data
  }

  /**
   * Scan a single page URL and add it to an existing scan
   * Pro feature only - counts against daily page scan limit
   *
   * @param url - The URL to scan (must be on the same domain as the site)
   * @param siteId - The site ID to add the page to
   * @returns The scanned page data, updated scores, and usage info
   */
  async scanSingleAIVisibilityPage(url: string, siteId: string): Promise<ApiResponse<{
    success: boolean;
    page?: any;
    scores?: {
      overall: number;
      access: number;
      content: number;
      structure: number;
      schema: number;
      alternate: number;
    };
    findings?: any[];
    usage?: {
      pageScansUsed: number;
      pageScansLimit: number;
    };
    isNewPage?: boolean;
    error?: string;
    existingPage?: any;
    message?: string;
  }>> {
    const response = await api.post('/ai-visibility/pages/scan', { url, siteId })
    return response.data
  }

  // ============================================================================
  // Alternate Files API (AI-readable .md and .txt files)
  // ============================================================================

  /**
   * Get AI-readable files for a URL
   */
  async getAlternateFiles(url: string): Promise<ApiResponse<{
    urlHash: string;
    cdnUrls?: { markdown: string; plaintext: string };
    linkTags?: { markdown: string; plaintext: string; combined: string };
    markdownPreview?: string;
    plaintextPreview?: string;
    isHosted: boolean;
    versionNumber?: number;
    lastContentUpdatedAt?: string;
  } | null>> {
    const response = await api.get('/alternate-files', { params: { url } })
    return response.data
  }

  /**
   * Get full alternate file content for download
   */
  async getAlternateFileContent(url: string, type: 'markdown' | 'plaintext'): Promise<string> {
    const response = await api.get('/alternate-files/content', {
      params: { url, type },
      responseType: 'text'
    })
    return response.data
  }

  /**
   * Generate AI-readable files for an existing URL (FREE for Pro users)
   */
  async generateAlternateFilesForExisting(
    url: string,
    organizationId?: string,
    schemaGenerationId?: string | null
  ): Promise<ApiResponse<{
    urlHash: string;
    cdnUrls?: { markdown: string; plaintext: string };
    linkTags?: { markdown: string; plaintext: string; combined: string };
    markdownPreview?: string;
    plaintextPreview?: string;
  }>> {
    const response = await api.post('/alternate-files/generate-existing', {
      url,
      organizationId,
      schemaGenerationId: schemaGenerationId || undefined,
    })
    return response.data
  }

  /**
   * Delete AI-readable files for a URL
   */
  async deleteAlternateFiles(url: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await api.delete('/alternate-files', { data: { url } })
    return response.data
  }

  // =============================================================================
  // AI BOT ANALYTICS (ABA) ENDPOINTS
  // =============================================================================

  /**
   * Get all tracked sites for the current team
   */
  async getAbaSites(): Promise<ApiResponse<{
    sites: Array<{
      id: string
      publicSiteId: string
      teamId: string
      createdByUserId: string
      domain: string
      name: string | null
      platform: string | null
      isActive: boolean
      isVerified: boolean
      verifiedAt: string | null
      createdAt: string
      updatedAt: string
      trackingPixelUrl: string
      trackingCodeSnippet: string
    }>
  }>> {
    const response = await api.get('/aba/sites')
    return response.data
  }

  /**
   * Create a new tracked site
   */
  async createAbaSite(data: {
    domain: string
    name?: string
    platform?: string
  }): Promise<ApiResponse<{
    site: {
      id: string
      publicSiteId: string
      domain: string
      name: string | null
      platform: string | null
      isActive: boolean
      createdAt: string
      trackingPixelUrl: string
      trackingCodeSnippet: string
    }
  }>> {
    const response = await api.post('/aba/sites', data)
    return response.data
  }

  /**
   * Get a specific site by ID
   */
  async getAbaSite(siteId: string): Promise<ApiResponse<{
    site: {
      id: string
      publicSiteId: string
      domain: string
      name: string | null
      platform: string | null
      isActive: boolean
      isVerified: boolean
      verifiedAt: string | null
      createdAt: string
      updatedAt: string
      trackingPixelUrl: string
      trackingCodeSnippet: string
    }
  }>> {
    const response = await api.get(`/aba/sites/${siteId}`)
    return response.data
  }

  /**
   * Update a tracked site
   */
  async updateAbaSite(siteId: string, data: {
    name?: string
    platform?: string
    isActive?: boolean
  }): Promise<ApiResponse<{
    site: any
  }>> {
    const response = await api.patch(`/aba/sites/${siteId}`, data)
    return response.data
  }

  /**
   * Delete a tracked site
   */
  async deleteAbaSite(siteId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await api.delete(`/aba/sites/${siteId}`)
    return response.data
  }

  /**
   * Verify tracking pixel installation
   */
  async verifyAbaSite(siteId: string): Promise<ApiResponse<{
    verified: boolean
    verifiedAt: string
    message: string
  }>> {
    const response = await api.post(`/aba/sites/${siteId}/verify`)
    return response.data
  }

  /**
   * Get analytics overview for a site
   */
  async getAbaOverview(siteId: string, days?: number): Promise<ApiResponse<{
    overview: {
      totalVisits: number
      uniqueBots: number
      uniquePages: number
      visitsByBot: Record<string, number>
      visitsTrend: number | null
      lifetimeVisits: number
    }
  }>> {
    const params = days ? `?days=${days}` : ''
    const response = await api.get(`/aba/sites/${siteId}/analytics/overview${params}`)
    return response.data
  }

  /**
   * Get paginated bot visits for a site
   */
  async getAbaVisits(siteId: string, options?: {
    page?: number
    limit?: number
  }): Promise<ApiResponse<{
    visits: Array<{
      id: string
      botName: string
      botCategory: string | null
      pageUrl: string | null
      pagePath: string | null
      ipCountry: string | null
      visitedAt: string
    }>
    total: number
    page: number
    limit: number
    hasMore: boolean
  }>> {
    const params = new URLSearchParams()
    if (options?.page) params.append('page', String(options.page))
    if (options?.limit) params.append('limit', String(options.limit))
    const queryString = params.toString()
    const response = await api.get(`/aba/sites/${siteId}/analytics/visits${queryString ? '?' + queryString : ''}`)
    return response.data
  }

  /**
   * Get top pages for a site
   */
  async getAbaTopPages(siteId: string, days?: number, limit?: number): Promise<ApiResponse<{
    pages: Array<{
      pagePath: string
      pageUrl: string | null
      pageTitle: string | null
      totalVisits: number
      uniqueBots: number
      lastVisitAt: string
      httpStatus: number | null
    }>
  }>> {
    const params = new URLSearchParams()
    if (days) params.append('days', String(days))
    if (limit) params.append('limit', String(limit))
    const queryString = params.toString()
    const response = await api.get(`/aba/sites/${siteId}/analytics/pages${queryString ? '?' + queryString : ''}`)
    return response.data
  }

  /**
   * Get bot breakdown for a site
   */
  async getAbaBotBreakdown(siteId: string, days?: number): Promise<ApiResponse<{
    bots: Array<{
      botName: string
      category: string
      visits: number
      percentage: number
    }>
  }>> {
    const params = days ? `?days=${days}` : ''
    const response = await api.get(`/aba/sites/${siteId}/analytics/bots${params}`)
    return response.data
  }

  /**
   * Get daily visits timeline for a site
   */
  async getAbaTimeline(siteId: string, days?: number): Promise<ApiResponse<{
    timeline: Array<{
      date: string
      totalVisits: number
      visitsByBot: Record<string, number>
    }>
  }>> {
    const params = days ? `?days=${days}` : ''
    const response = await api.get(`/aba/sites/${siteId}/analytics/timeline${params}`)
    return response.data
  }

  /**
   * Get list of known AI bots
   */
  async getAbaBotRegistry(): Promise<ApiResponse<{
    bots: Array<{
      id: number
      name: string
      displayName: string
      company: string
      category: string
      userAgentPattern: string
      documentationUrl: string | null
      isActive: boolean
    }>
  }>> {
    const response = await api.get('/aba/bots')
    return response.data
  }

  async getAbaCommunityStats(): Promise<ApiResponse<{
    visitsLastHour: number
    newSitesToday: number
    firstBotCelebrationsToday: number
    mostActiveBot: { name: string; visits: number } | null
  }>> {
    const response = await api.get('/aba/community-stats')
    return response.data
  }

  // ==========================================================================
  // Discovery Network API
  // ==========================================================================

  /**
   * Get all discovery network entries for the current user's team
   */
  async getDiscoveryEntries(): Promise<ApiResponse<{
    entries: Array<{
      id: string
      domain: string
      slug: string
      businessName: string | null
      businessDescription: string | null
      industry: string | null
      location: string | null
      schemaCoverage: Record<string, number>
      totalPagesWithSchema: number
      keyPages: Array<{ url: string; title: string; type: string }>
      includedInNetwork: boolean
      registryBotVisits: Record<string, number>
      lastRegistryBotVisitAt: string | null
      lastSchemaGeneratedAt: string | null
      createdAt: string
    }>
  }>> {
    const response = await api.get('/discovery/entries')
    return response.data
  }

  /**
   * Get discovery network statistics for the current user's team
   */
  async getDiscoveryStats(): Promise<ApiResponse<{
    stats: {
      totalEntries: number
      activeEntries: number
      totalBotVisits: Record<string, number>
      lastBotVisit: string | null
      entries: Array<{
        domain: string
        slug: string
        registryUrl: string
        includedInNetwork: boolean
        botVisits: Record<string, number>
        lastBotVisit: string | null
        lastUpdated: string | null
      }>
    }
  }>> {
    const response = await api.get('/discovery/stats')
    return response.data
  }

  /**
   * Toggle opt-out status for a discovery network entry
   */
  async toggleDiscoveryOptOut(domain: string, included: boolean): Promise<ApiResponse<{
    domain: string
    includedInNetwork: boolean
  }>> {
    const response = await api.patch(`/discovery/entries/${encodeURIComponent(domain)}/opt-out`, {
      included
    })
    return response.data
  }

  // Site Scan Monitoring endpoints (Admin)
  async getSiteScanLogs(params?: {
    page?: number
    limit?: number
    status?: string
    scanType?: string
    domain?: string
    userId?: string
    startDate?: string
    endDate?: string
  }): Promise<ApiResponse<{
    logs: Array<{
      id: string
      scan_id: string
      site_id?: string
      user_id: string
      user_email?: string
      team_id?: string
      domain: string
      scan_type: 'quick' | 'full'
      is_pro: boolean
      max_pages?: number
      concurrency?: number
      timeout_ms?: number
      pages_discovered: number
      pages_analyzed: number
      pages_failed: number
      started_at: string
      completed_at?: string
      duration_ms?: number
      avg_page_time_ms?: number
      status: 'pending' | 'scanning' | 'completed' | 'partial' | 'failed' | 'cancelled'
      overall_score?: number
      error_code?: string
      error_message?: string
      error_stage?: string
      progress_poll_failures: number
      event_loop_yields: number
      health_check_failures: number
      ip_address?: string
      user_agent?: string
      diagnostic_context: Record<string, any>
      created_at: string
      updated_at: string
    }>
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.status) searchParams.set('status', params.status)
    if (params?.scanType) searchParams.set('scanType', params.scanType)
    if (params?.domain) searchParams.set('domain', params.domain)
    if (params?.userId) searchParams.set('userId', params.userId)
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)
    const response = await api.get(`/admin/site-scans?${searchParams.toString()}`)
    return response.data
  }

  async getSiteScanLogStats(): Promise<ApiResponse<{
    total: number
    byStatus: Record<string, number>
    byType: Record<string, number>
    avgDurationMs: number
    avgPagesAnalyzed: number
    failureRate: number
    last24Hours: {
      total: number
      completed: number
      failed: number
      partial: number
    }
    topErrors: Array<{
      error_code: string
      error_message: string
      count: number
    }>
  }>> {
    const response = await api.get('/admin/site-scans/stats')
    return response.data
  }

  async getSiteScanLog(scanId: string): Promise<ApiResponse<{
    id: string
    scan_id: string
    site_id?: string
    user_id: string
    user_email?: string
    domain: string
    scan_type: 'quick' | 'full'
    is_pro: boolean
    pages_discovered: number
    pages_analyzed: number
    pages_failed: number
    started_at: string
    completed_at?: string
    duration_ms?: number
    status: string
    overall_score?: number
    error_code?: string
    error_message?: string
    error_stage?: string
    diagnostic_context: Record<string, any>
    created_at: string
  }>> {
    const response = await api.get(`/admin/site-scans/${scanId}`)
    return response.data
  }

  // =================================================================
  // Growth Analytics - Admin Endpoints
  // =================================================================

  /** The onboarding funnel, one journey per account: the growth board's funnel once sign-up comes first. */
  async getAccountGrowthFunnel(days: 7 | 30 | 90 = 30): Promise<{ success: boolean; data?: AccountFunnelAnalytics; error?: string; message?: string }> {
    const response = await api.get('/admin/analytics/growth/account-funnel', { params: { days } })
    return response.data
  }

  /** The HubSpot onboarding funnel: the growth board's funnel. */
  async getHubSpotGrowthFunnel(days: 7 | 30 | 90 = 30, device: 'all' | 'desktop' | 'mobile' = 'all', site: 'all' | 'hubspot' | 'other' = 'all'): Promise<{ success: boolean; data?: HubSpotFunnelAnalytics; error?: string; message?: string }> {
    const response = await api.get('/admin/analytics/growth/hubspot-funnel', { params: { days, device, site } })
    return response.data
  }

  /** One session's report, read again now, for the growth board's full-screen view. */
  async getGrowthSessionReport(sessionId: string): Promise<{ success: boolean; data?: GrowthSessionReport; error?: string }> {
    const response = await api.get(`/admin/analytics/growth/hubspot-funnel/sessions/${encodeURIComponent(sessionId)}/report`)
    return response.data
  }

  async getSiteScanGrowthFunnel(
    days: 7 | 30 | 90 = 30,
    device: 'all' | 'desktop' | 'mobile' = 'all'
  ): Promise<ApiResponse<{
    period: string
    device?: 'all' | 'desktop' | 'mobile'
    cohortStartDate?: string
    cohortEndDate?: string
    launchDate?: string
    stages: Array<{
      key: 'free_site_scan' | 'basic_results' | 'claimed_results' | 'packages_seen' | 'plans_opened' | 'plan_selected' | 'trial_started' | 'subscription'
      label: string
      count: number
      conversionFromPrevious: number
      conversionFromStart: number
      dropoffFromPrevious: number
    }>
    dropoffs: Array<{
      key: string
      label: string
      count: number
      rate: number
      recommendedAction: string
    }>
    activationSignals: Array<{
      key: string
      label: string
      count: number
      rateFromClaimed: number
      rateFromEngaged: number
      description: string
      recommendedAction: string
    }>
    timing: {
      scanToResultsMinutes: { avg: number; median: number } | null
      resultsToAccountMinutes: { avg: number; median: number } | null
      accountToPlanSelectedMinutes: { avg: number; median: number } | null
      planSelectedToSubscriptionHours: { avg: number; median: number } | null
    }
    dailyTrend: Array<{
      date: string
      scanStarts: number
      basicResults: number
      claimedResults?: number
      accountCreated: number
      resultsViewed: number
      pageSetups: number
      subscriptions: number
    }>
    planConversions: Record<string, number> & { total: number }
    onboardingSubscriptionCohort: {
      cohortStartDate: string
      scanned: number
      claimed: number
      subscribed: number
      claimRate: number
      subscriptionRate: number
      subscriptionRateFromScan: number
      medianDaysToSubscribe: number | null
      subscribers: Array<{
        userId: string
        email: string | null
        domain: string
        tier: string
        claimedAt: string
        subscribedAt: string
        daysToSubscribe: number
      }>
      note: string
    }
    preSignupGateFunnel: {
      resultsReady: number
      businessConfirmed: number
      gateReached: number
      claimed: number
      businessConfirmedRate: number
      gateReachedRate: number
      gateConversionRate: number
      note: string
    }
    directSignupSummary: {
      count: number
      note: string
      recentUsers: Array<{ id: string; email: string | null; createdAt: string }>
    }
    dataQuality: {
      resultsViewed: {
        real: number
        estimated: number
        note: string
      }
    }
    claimedReportInsights?: {
      totalClaimedReports: number
      avgSiteScanScore: number | null
      avgAIRecommendationScore: number | null
      lowAIRecommendationCount: number
      providerIssueCount: number
      reports: Array<{
        sessionId: string
        hasClaimedReport: boolean
        siteScanScore: number | null
        siteScanGrade: string | null
        pagesAnalyzed: number | null
        aiRecommendationScore: number | null
        aiRecommendationGrade: string | null
        aiScoreStatus: 'ready' | 'calculating' | 'not_captured'
        citationCounts: {
          cited: number
          offering: number
          notCited: number
          providerIssue: number
          pending: number
          total: number
        }
        customerSeenAIRecommendation: {
          score: number | null
          grade: string | null
          status: 'ready' | 'calculating' | 'not_captured'
          citationCounts: {
            cited: number
            offering: number
            notCited: number
            providerIssue: number
            pending: number
            total: number
          }
          scoredChecks: number | null
          totalChecks: number | null
          snapshotAt: string
        } | null
        providerIssueCount: number
        intentSummary: {
          primaryGoal: string | null
          websiteManager: string | null
          clientSiteCount: string | null
          depthPreference: string | null
          discoverySource: string | null
          discoverySourceOther: string | null
        } | null
        domain: string
        email: string | null
        claimedAt: string | null
        lastActivityAt: string
      }>
    }
    recentSessions: Array<{
      sessionId: string
      domain: string
      email: string | null
      currentStage: string
      lastActivityAt: string
      nextAction: string
      claimedReportSummary?: {
        sessionId: string
        hasClaimedReport: boolean
        siteScanScore: number | null
        siteScanGrade: string | null
        pagesAnalyzed: number | null
        aiRecommendationScore: number | null
        aiRecommendationGrade: string | null
        aiScoreStatus: 'ready' | 'calculating' | 'not_captured'
        citationCounts: {
          cited: number
          offering: number
          notCited: number
          providerIssue: number
          pending: number
          total: number
        }
        customerSeenAIRecommendation: {
          score: number | null
          grade: string | null
          status: 'ready' | 'calculating' | 'not_captured'
          citationCounts: {
            cited: number
            offering: number
            notCited: number
            providerIssue: number
            pending: number
            total: number
          }
          scoredChecks: number | null
          totalChecks: number | null
          snapshotAt: string
        } | null
        providerIssueCount: number
        intentSummary: {
          primaryGoal: string | null
          websiteManager: string | null
          clientSiteCount: string | null
          depthPreference: string | null
          discoverySource: string | null
          discoverySourceOther: string | null
        } | null
      }
    }>
    intentSegments?: {
      totalResponses: number
      primaryGoal: Record<string, number>
      websiteManager: Record<string, number>
      clientSiteCount: Record<string, number>
      depthPreference: Record<string, number>
      discoverySource: Record<string, number>
      recentResponses: Array<{
        userId: string
        email: string | null
        domain: string | null
        primaryGoal: string
        websiteManager: string | null
        clientSiteCount: string | null
        depthPreference: string
        discoverySource: string | null
        discoverySourceOther: string | null
        createdAt: string
      }>
    }
    exclusions?: {
      excludedDomains: string[]
      excludedSessionCount: number
      note: string
    }
  }>> {
    const response = await api.get(`/admin/analytics/growth/site-scan-funnel?days=${days}&device=${device}`)
    return response.data
  }

  async getGrowthClaimedReport(sessionId: string): Promise<ApiResponse<{
    available: boolean
    reason?: string
    session: {
      sessionId: string
      domain: string
      status: string | null
      createdAt: string | null
      claimedAt: string | null
      claimedScanId: string | null
    }
    user: { id: string; email: string | null } | null
    scanSummary?: {
      score: number | null
      grade: string | null
      pagesAnalyzed: number | null
      status: string | null
      completedAt: string | null
      categoryScores: Record<string, number | null> | null
    }
    aiRecommendation?: {
      score: number | null
      grade: string | null
      status: 'ready' | 'calculating' | 'not_captured'
      citationCounts: {
        cited: number
        offering: number
        notCited: number
        providerIssue: number
        pending: number
        total: number
      }
      providerIssueCount: number
    }
    context?: {
      companyName: string | null
      businessCategory: string | null
      audience: string | null
      offerings: string[]
      possibleCompetitors: string[]
      overview: string | null
    } | null
    intent?: {
      primaryGoal: string | null
      primaryGoalOther: string | null
      websiteManager: string | null
      clientSiteCount: string | null
      depthPreference: string | null
      discoverySource: string | null
      discoverySourceOther: string | null
      createdAt: string | null
    } | null
    questions?: Array<{
      question: string
      answers: Array<{
        id: string
        provider: string
        status: string
        outcome: 'cited' | 'offering' | 'not_cited' | 'provider_issue' | 'pending'
        answerSummary: string | null
        mentionsSite: boolean | null
        confidence: string | null
        errorMessage: string | null
        completedAt: string | null
      }>
    }>
  }>> {
    const response = await api.get(`/admin/analytics/growth/claimed-report/${encodeURIComponent(sessionId)}`)
    return response.data
  }

  async trackEngagementEvent(data: {
    eventName: ProductEngagementEventName
    domainId?: string | null
    discoveredUrlId?: string | null
    metadata?: Record<string, unknown>
  }): Promise<ApiResponse<{ tracked: boolean }>> {
    const response = await api.post('/engagement/events', data)
    return response.data
  }

  /**
   * Get simplified 3-stage growth funnel: New Users → Active Users → Paid Users
   * Uses actual signups from users table, includes both subscriptions and credit pack purchases
   */
  async getSimplifiedGrowthFunnel(days: 7 | 30 | 90 = 30): Promise<ApiResponse<{
    period: string
    funnel: {
      newUsers: number
      activeUsers: number
      paidUsers: number
    }
    conversionRates: {
      newToActive: number
      activeToPaid: number
      overall: number
    }
    paidBreakdown: {
      subscribers: number
      creditPackBuyers: number
    }
    dropoffs: {
      signedUpNotScanned: number
      scannedNotPaid: number
    }
  }>> {
    const response = await api.get(`/admin/analytics/growth/simplified-funnel?days=${days}`)
    return response.data
  }

  async getCampaignAnalytics(days: number | null): Promise<ApiResponse<{
    days: number | null
    rows: Array<{
      campaign: string
      source: string
      medium: string
      signups: number
      paidConversions: number
      conversionRate: number
      firstSignupAt: string | null
      lastSignupAt: string | null
    }>
    totals: { signups: number; paidConversions: number }
  }>> {
    const response = await api.get(`/admin/analytics/campaigns${days ? `?days=${days}` : ''}`)
    return response.data
  }

  async getGrowthFunnel(days: 7 | 30 | 90 = 30): Promise<ApiResponse<{
    period: string
    metrics: {
      totalSignups: number
      startedScan: number
      completedScan: number
      usedFirstCredit: number
      usedSecondCredit: number
      upgradeAttempted: number
      upgradeCompleted: number
    }
    conversionRates: {
      signupToScan: number
      scanToComplete: number
      completeToFirstCredit: number
      firstToSecondCredit: number
      creditToUpgrade: number
      overallConversion: number
    }
  }>> {
    const response = await api.get(`/admin/analytics/growth/funnel?days=${days}`)
    return response.data
  }

  async getGrowthCohorts(weeks: number = 12): Promise<ApiResponse<{
    weeks: number
    cohorts: Array<{
      weekCohort: string
      signups: number
      startedScan: number
      completedScan: number
      usedFirstCredit: number
      usedSecondCredit: number
      upgradeCompleted: number
      conversionRates: {
        signupToScan: number
        scanToComplete: number
        completeToFirstCredit: number
        creditToUpgrade: number
        overallConversion: number
      }
    }>
  }>> {
    const response = await api.get(`/admin/analytics/growth/cohorts?weeks=${weeks}`)
    return response.data
  }

  async getGrowthTiming(): Promise<ApiResponse<{
    signupToScan: { avg: number; median: number; unit: string } | null
    scanToFirstCredit: { avg: number; median: number; unit: string } | null
    firstToSecondCredit: { avg: number; median: number; unit: string } | null
    signupToUpgrade: { avg: number; median: number; unit: string } | null
  }>> {
    const response = await api.get('/admin/analytics/growth/timing')
    return response.data
  }

  async getGrowthSegments(): Promise<ApiResponse<{
    ctaPerformance: Array<{
      componentName: string
      totalClicks: number
      uniqueUsers: number
      conversionRate: number
    }>
  }>> {
    const response = await api.get('/admin/analytics/growth/segments')
    return response.data
  }

  async getGrowthUserJourney(userId: string): Promise<ApiResponse<{
    user: {
      id: string
      email: string
      createdAt: string
    }
    events: Array<{
      eventType: string
      createdAt: string
      metadata: Record<string, unknown>
    }>
    summary: {
      totalEvents: number
      lastEventAt: string | null
      completedStages: string[]
    }
  }>> {
    const response = await api.get(`/admin/analytics/growth/user-journey/${userId}`)
    return response.data
  }

  async getGrowthAlerts(): Promise<ApiResponse<{
    completedScanNoCredit: {
      description: string
      count: number
      users: Array<{ userId: string; email: string; completedAt: string }>
    }
    usedCreditsNoUpgrade: {
      description: string
      count: number
      users: Array<{ userId: string; email: string; lastCreditAt: string }>
    }
  }>> {
    const response = await api.get('/admin/analytics/growth/alerts')
    return response.data
  }

  async trackUpgradeCTA(data: {
    userId?: string // Deprecated: userId is now derived from auth token
    componentName: string
    action?: string
    pagePath?: string
    metadata?: Record<string, unknown>
  }): Promise<ApiResponse<{ message: string }>> {
    // Note: userId is ignored - backend uses authenticated user's ID for security
    const { userId, ...trackingData } = data
    const response = await api.post('/user/track-cta', trackingData)
    return response.data
  }

  async getPathAwareFunnel(days: 7 | 30 | 90 = 30): Promise<ApiResponse<{
    period: string
    stageCounts: {
      signup: number
      startedScan: number
      completedScan: number
      usedFirstCredit: number
      usedSecondCredit: number
      upgradeAttempted: number
      upgradeCompleted: number
    }
    pathConversions: {
      signupToScan: { eligible: number; converted: number; rate: number }
      scanToComplete: { eligible: number; converted: number; rate: number }
      completeToFirstCredit: { eligible: number; converted: number; rate: number }
      firstToSecondCredit: { eligible: number; converted: number; rate: number }
      secondCreditToUpgrade: { eligible: number; converted: number; rate: number }
      overallSignupToUpgrade: { eligible: number; converted: number; rate: number }
    }
    upgradeSegments: {
      standardPath: number
      directFromScan: number
      directFromSignup: number
      withCreditsRemaining: number
    }
  }>> {
    const response = await api.get(`/admin/analytics/growth/path-aware?days=${days}`)
    return response.data
  }

  async getFunnelSegmentUsers(days: 7 | 30 | 90 = 30): Promise<ApiResponse<{
    period: string
    upgradePathUsers: {
      standardPath: {
        description: string
        count: number
        users: Array<{ userId: string; email: string; signupAt: string; upgradeAt: string }>
      }
      partialCreditPath: {
        description: string
        count: number
        users: Array<{ userId: string; email: string; signupAt: string; upgradeAt: string }>
      }
      directFromScan: {
        description: string
        count: number
        users: Array<{ userId: string; email: string; signupAt: string; upgradeAt: string }>
      }
      directFromSignup: {
        description: string
        count: number
        users: Array<{ userId: string; email: string; signupAt: string; upgradeAt: string }>
      }
    }
    stuckUsers: {
      stuckAfterScan: {
        description: string
        count: number
        users: Array<{ userId: string; email: string; scanCompletedAt: string }>
      }
      stuckAfterFirstCredit: {
        description: string
        count: number
        users: Array<{ userId: string; email: string; firstCreditAt: string }>
      }
      stuckAfterSecondCredit: {
        description: string
        count: number
        users: Array<{ userId: string; email: string; secondCreditAt: string }>
      }
    }
  }>> {
    const response = await api.get(`/admin/analytics/growth/segment-users?days=${days}`)
    return response.data
  }
  // ==========================================
  // Unified Domains
  // ==========================================

  async getUnifiedDomains(): Promise<ApiResponse<UnifiedDomain[]>> {
    const response = await api.get('/domains')
    return response.data
  }

  async createUnifiedDomain(domain: string, displayName?: string): Promise<ApiResponse<UnifiedDomain>> {
    const response = await api.post('/domains', { domain, displayName })
    return response.data
  }

  async updateUnifiedDomain(domainId: string, displayName: string | null): Promise<ApiResponse<UnifiedDomain>> {
    const response = await api.patch(`/domains/${domainId}`, { displayName })
    return response.data
  }

  async reorderDomains(orderings: { id: string; displayOrder: number }[]): Promise<ApiResponse<void>> {
    const response = await api.put('/domains/reorder', { orderings })
    return response.data
  }

  async deleteUnifiedDomain(domainId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await api.delete(`/domains/${domainId}`)
    return response.data
  }

  async getDomainDeletionPreview(domainId: string): Promise<ApiResponse<import('aeo-schema-generator-shared/types').DomainDeletionPreview>> {
    const response = await api.get(`/domains/${domainId}/deletion-preview`)
    return response.data
  }

  async getDomainLimits(): Promise<ApiResponse<import('aeo-schema-generator-shared/types').DomainLimits>> {
    const response = await api.get('/domains/limits')
    return response.data
  }

  async resolvePageSetupUrl(url: string): Promise<ApiResponse<import('aeo-schema-generator-shared/types').PageSetupResolveResponse>> {
    const response = await api.post('/domains/page-setup/resolve', { url })
    return response.data
  }

  async getDomainSummary(domainId: string, subdomain?: string): Promise<ApiResponse<import('aeo-schema-generator-shared/types').DomainDetailSummary>> {
    const response = await api.get(`/domains/${domainId}/summary`, {
      params: subdomain ? { subdomain } : undefined
    })
    return response.data
  }

  async getDomainCitation(domainId: string, subdomain?: string): Promise<ApiResponse<import('aeo-schema-generator-shared/types').DomainCitationRunDetail>> {
    const response = await api.get(`/domains/${domainId}/citation`, {
      params: subdomain ? { subdomain } : undefined
    })
    return response.data
  }

  async getDomainCitationQuestions(domainId: string, subdomain?: string): Promise<ApiResponse<{
    questions: string[]
    context: import('aeo-schema-generator-shared/types').DomainCitationEditableContext
    summary: import('aeo-schema-generator-shared/types').DomainCitationSummary
  }>> {
    const response = await api.get(`/domains/${domainId}/citation/questions`, {
      params: subdomain ? { subdomain } : undefined
    })
    return response.data
  }

  async startDomainCitationRescan(
    domainId: string,
    questions: string[],
    context?: import('aeo-schema-generator-shared/types').DomainCitationEditableContext,
    subdomain?: string
  ): Promise<ApiResponse<import('aeo-schema-generator-shared/types').DomainCitationSummary>> {
    const response = await api.post(`/domains/${domainId}/citation/rescan`, { questions, context }, {
      params: subdomain ? { subdomain } : undefined
    })
    return response.data
  }

  async getDomainCitationRun(domainId: string, runId: string, subdomain?: string): Promise<ApiResponse<import('aeo-schema-generator-shared/types').DomainCitationRunDetail>> {
    const response = await api.get(`/domains/${domainId}/citation/runs/${runId}`, {
      params: subdomain ? { subdomain } : undefined
    })
    return response.data
  }

  async getDomainSubdomains(domainId: string): Promise<ApiResponse<{ subdomain: string; sources: string[] }[]>> {
    const response = await api.get(`/domains/${domainId}/subdomains`)
    return response.data
  }

  async getDomainUrls(domainId: string, params: {
    filter?: string
    search?: string
    page?: number
    pageSize?: number
    subdomain?: string
  } = {}): Promise<ApiResponse<import('aeo-schema-generator-shared/types').DomainDetailUrlsResponse>> {
    const response = await api.get(`/domains/${domainId}/urls`, { params })
    return response.data
  }

  async getUnifiedPageDetail(
    domainId: string,
    path: string,
    opts?: { libraryUrlId?: string; scanPageId?: string; schemaGenerationId?: string; url?: string }
  ): Promise<ApiResponse<{
    url: string
    domain: string
    path: string
    hasScannedData: boolean
    hasSchemaData: boolean
    hasLibraryData: boolean
    scan: any | null
    schemaGenerations: any[]
    alternateFiles: any[]
    library: any | null
    coverage?: SchemaCoverage | null
  }>> {
    // Explicit IDs let the backend resolve the exact row the user clicked.
    // `libraryUrlId` covers saved/generated URLs; `scanPageId` covers
    // scan-only report rows whose live URLs can include query strings,
    // canonical variants, or subdomains that path-only matching can miss.
    const params: Record<string, string> = { path }
    if (opts?.libraryUrlId) params.libraryUrlId = opts.libraryUrlId
    if (opts?.scanPageId) params.scanPageId = opts.scanPageId
    if (opts?.schemaGenerationId) params.schemaGenerationId = opts.schemaGenerationId
    if (opts?.url) params.url = opts.url
    const response = await api.get(`/domains/${domainId}/pages/by-path`, { params })
    return response.data
  }

  // ─── Monitoring (Phase 2) ──────────────────────────────────────────────────

  async getMonitoringStatus(domainId: string): Promise<ApiResponse<{
    domainId: string
    domain: string
    monitoringEnabled: boolean
    monitoringHealth: string
    openDriftEvents: number
    totalDriftEvents: number
    lastMonitoringRun: string | null
    monitoredUrlCount: number
    llmsTxtEnabled: boolean
  }>> {
    const response = await api.get(`/monitoring/status/${domainId}`)
    return response.data
  }

  async getDriftEvents(domainId: string, params: {
    status?: string
    limit?: number
    offset?: number
  } = {}): Promise<ApiResponse<any[]>> {
    const response = await api.get(`/monitoring/drift/${domainId}`, { params })
    return response.data
  }

  async getDriftEventDetail(domainId: string, eventId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/monitoring/drift/${domainId}/${eventId}`)
    return response.data
  }

  async resolveDriftEvent(eventId: string, note?: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/monitoring/drift/${eventId}/resolve`, { note })
    return response.data
  }

  async previewMonitoringLiveUpdate(urlId: string): Promise<ApiResponse<{
    urlId: string
    requestedUrl: string
    finalUrl: string
    fetchedAt: string
    title: string | null
    description: string | null
    excerpt: string
    wordCount: number | null
    schemaTypes: string[]
    alternateLinks: {
      markdown: string | null
      plaintext: string | null
    }
  }>> {
    const response = await api.post(`/monitoring/urls/${urlId}/live-update/preview`)
    return response.data
  }

  async dismissDriftEvent(eventId: string, reason?: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/monitoring/drift/${eventId}/dismiss`, { reason })
    return response.data
  }

  async getMonitoringHistory(domainId: string, limit = 20): Promise<ApiResponse<any[]>> {
    const response = await api.get(`/monitoring/history/${domainId}`, { params: { limit } })
    return response.data
  }

  async getMonitoringPreferences(domainId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/monitoring/preferences/${domainId}`)
    return response.data
  }

  async updateMonitoringPreferences(domainId: string, prefs: {
    alert_frequency?: string
    alert_email?: string
    min_severity?: string
    monitoring_enabled?: boolean
  }): Promise<ApiResponse<any>> {
    const response = await api.put(`/monitoring/preferences/${domainId}`, prefs)
    return response.data
  }

  async toggleDomainMonitoring(domainId: string, enabled: boolean): Promise<ApiResponse<any>> {
    const response = await api.post(`/monitoring/toggle/${domainId}`, { enabled })
    return response.data
  }

  async resetBaseline(urlId: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/monitoring/baseline/${urlId}/reset`)
    return response.data
  }

  async verifySchema(urlId: string): Promise<ApiResponse<{
    verified: boolean
    baselineRefresh?:
      | { refreshed: true; resolvedEventCount: number }
      | { refreshed: false; reason: 'not_requested' | 'not_active' | 'url_not_found' | 'capture_failed'; error?: string }
    verificationScope?: 'full_package' | 'schema_only'
    liveSchemaTypes: string[]
    expectedSchemaTypes: string[]
    matchedTypes: string[]
    missingTypes: string[]
    expectedSchemaVersionId?: string | null
    liveSchemaVersionId?: string | null
    schemaVersionMatched?: boolean
    expectedAlternateFiles?: string[]
    matchedAlternateFiles?: string[]
    missingAlternateFiles?: string[]
    unconfirmedAlternateFiles?: string[]
    alternateVersionMismatches?: string[]
    verificationState?: 'verified' | 'verified_compatible' | 'blocked_or_unreachable' | 'schema_missing' | 'alternate_files_missing' | 'alternate_files_unconfirmed' | 'stale_package' | 'wrong_page_or_redirect'
    accessStatus?: 'ok' | 'blocked_http' | 'robots_disallowed' | 'timeout' | 'challenge' | 'redirect_mismatch' | 'unreachable' | 'unknown'
    httpStatus?: number
    finalUrl?: string | null
    robotsTxtUrl?: string | null
    robotsAllowed?: boolean | null
    recommendedFixes?: string[]
    verificationSteps?: Array<{
      key: string
      label: string
      status: 'passed' | 'failed' | 'skipped' | 'unknown'
      details?: string
    }>
    details: string
  }>> {
    const response = await api.post(`/monitoring/verify/${urlId}`)
    return response.data
  }

  async getInstallPackage(urlId: string): Promise<ApiResponse<{
    html: string
    schemaGenerationIds: string[]
    schemaTypes: string[]
    schemaPackageHash: string
    alternateFiles: Array<{
      format: 'markdown' | 'plaintext'
      href: string
      versionId: string
    }>
  }>> {
    const response = await api.get(`/monitoring/install-package/${urlId}`)
    return response.data
  }

  async detectDeployment(urlId: string, mode: 'immediate' | 'poll' = 'immediate'): Promise<ApiResponse<{
    mode: string
    detected?: boolean
    liveSchemaTypes?: string[]
    expectedSchemaTypes?: string[]
    matchedTypes?: string[]
    missingTypes?: string[]
    expectedSchemaVersionId?: string | null
    liveSchemaVersionId?: string | null
    schemaVersionMatched?: boolean
    expectedAlternateFiles?: string[]
    matchedAlternateFiles?: string[]
    missingAlternateFiles?: string[]
    unconfirmedAlternateFiles?: string[]
    alternateVersionMismatches?: string[]
    verificationState?: 'verified' | 'verified_compatible' | 'blocked_or_unreachable' | 'schema_missing' | 'alternate_files_missing' | 'alternate_files_unconfirmed' | 'stale_package' | 'wrong_page_or_redirect'
    accessStatus?: 'ok' | 'blocked_http' | 'robots_disallowed' | 'timeout' | 'challenge' | 'redirect_mismatch' | 'unreachable' | 'unknown'
    httpStatus?: number
    finalUrl?: string | null
    robotsTxtUrl?: string | null
    robotsAllowed?: boolean | null
    recommendedFixes?: string[]
    verificationSteps?: Array<{
      key: string
      label: string
      status: 'passed' | 'failed' | 'skipped' | 'unknown'
      details?: string
    }>
    details?: string
    activation?: { activated: boolean; reason?: string; monitoredCount?: number; cap?: number } | null
    started?: boolean
    error?: string
  }>> {
    const response = await api.post(`/monitoring/detect/${urlId}?mode=${mode}`)
    return response.data
  }

  async sweepDomainDeployment(domainId: string): Promise<ApiResponse<{ started: boolean }>> {
    const response = await api.post(`/monitoring/sweep-deployment/${domainId}`)
    return response.data
  }

  async getTeamMonitoringCap(): Promise<ApiResponse<{
    cap: number
    count: number
  }>> {
    const response = await api.get('/monitoring/cap')
    return response.data
  }

  /**
   * Explicit "Activate monitoring on this URL" — captures a baseline
   * and sets monitoring_status='active'. Precondition is
   * deployment_status='verified' (server-enforced). Returns 409 with
   * reason='cap_reached' when at cap so the UI can open
   * SwapMonitoringDialog.
   */
  async activateMonitoring(urlId: string): Promise<ApiResponse<{
    activated: boolean
    reason?: 'cap_reached' | 'not_detected' | 'baseline_capture_failed'
    monitoredCount?: number
    cap?: number
  }>> {
    const response = await api.post(`/monitoring/activate/${urlId}`)
    return response.data
  }

  /**
   * Monitor-only mode: watch the page's OWN schema; SuperSchema never
   * deploys. Returns 409 with data.reason='cap_reached' when at cap, and
   * 422 with data.reason='no_schema_found' | 'superschema_installed' when
   * the page cannot be observed.
   */
  async activateObservedMonitoring(urlId: string): Promise<ApiResponse<{
    activated: boolean
    reason?: 'cap_reached' | 'not_detected' | 'baseline_capture_failed' | 'no_schema_found' | 'superschema_installed' | 'monitoring_disabled'
    monitoredCount?: number
    cap?: number
  }>> {
    const response = await api.post(`/monitoring/activate-observed/${urlId}`)
    return response.data
  }

  /**
   * Observed pages: accept the current live schema as the new baseline.
   * Resolves open drift events for the page.
   */
  async rebaselineObservedMonitoring(urlId: string): Promise<ApiResponse<{
    ok: boolean
    reason?: 'capture_failed' | 'no_schema_found' | 'not_observed'
  }>> {
    const response = await api.post(`/monitoring/rebaseline/${urlId}`)
    return response.data
  }

  /**
   * Explicit "Stop monitoring this URL". Frees the slot but keeps the URL
   * verified. Marks the active baseline inactive so re-activating later
   * captures fresh state.
   */
  async deactivateMonitoring(urlId: string): Promise<ApiResponse<{ deactivated: boolean }>> {
    const response = await api.post(`/monitoring/deactivate/${urlId}`)
    return response.data
  }

  /**
   * Bulk-activate monitoring across many verified URLs. Powers the
   * legacy onboarding picker. Server enforces a 500-URL cap per call.
   */
  async bulkActivateMonitoring(urlIds: string[]): Promise<ApiResponse<{
    activated: Array<{ urlId: string; monitoredCount?: number; cap?: number }>
    skipped: Array<{ urlId: string; reason: string }>
  }>> {
    const response = await api.post('/monitoring/bulk-activate', { urlIds })
    return response.data
  }

  /**
   * List every URL on the team that's verified but not yet activated
   * for monitoring. Powers the bulk picker for legacy customers who
   * have N verified URLs and need to pick up to `cap` to monitor.
   */
  async getVerifiedNotActiveUrls(): Promise<ApiResponse<{
    urls: Array<{
      id: string
      url: string
      path: string | null
      domainId: string
      domainHostname: string | null
      lastSchemaGeneratedAt: string | null
    }>
    totalCount: number
    truncated: boolean
  }>> {
    const response = await api.get('/monitoring/verified-not-active')
    return response.data
  }

  async getReadabilityScore(urlId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/monitoring/readability/${urlId}`)
    return response.data
  }

  async generateLlmsTxt(domainId: string): Promise<ApiResponse<{ id: string; content: string; urlCount: number }>> {
    const response = await api.post(`/monitoring/llms-txt/${domainId}/generate`)
    return response.data
  }

  async getLlmsTxt(domainId: string): Promise<ApiResponse<{ content: string }>> {
    const response = await api.get(`/monitoring/llms-txt/${domainId}`)
    return response.data
  }

  /**
   * Enqueue a one-off manual drift recheck for a single URL.
   * Rate-limited server-side (5 min cooldown per URL) — will 429 if called too soon.
   */
  async manualRecheck(urlId: string): Promise<ApiResponse<{
    enqueuedAt: string
    cooldownSec: number
  }>> {
    const response = await api.post(`/monitoring/recheck/${urlId}`)
    return response.data
  }

  /**
   * Poll for manual recheck completion.
   * Pass the `enqueuedAt` returned from `manualRecheck` as `since`.
   * Returns `status: 'running'` until the crawl+diff completes, then `status: 'complete'`
   * with the new drift events detected (if any).
   */
  async getRecheckStatus(urlId: string, since: string): Promise<ApiResponse<{
    status: 'running' | 'complete'
    driftCount?: number
    driftStatus?: string
    activeDriftCount?: number
    newEvents?: Array<{
      id: string
      severity: string
      module: string
      summary: string | null
    }>
  }>> {
    const response = await api.get(`/monitoring/recheck/${urlId}/status`, {
      params: { since },
    })
    return response.data
  }

  /**
   * Accept the current page state as the new baseline.
   * Promotes the most recent snapshot to an active baseline + auto-resolves
   * all open drift events for the URL.
   */
  async acceptBaseline(urlId: string): Promise<ApiResponse<{
    newBaselineId?: string
    resolvedEventCount: number
  }>> {
    const response = await api.post(`/monitoring/drift/${urlId}/accept-baseline`)
    return response.data
  }

  /**
   * "Check all pages now" — fan-out recheck for every eligible URL on the
   * domain. Respects per-URL cooldown silently.
   */
  async recheckAll(domainId: string): Promise<ApiResponse<{
    totalEligible: number
    enqueued: number
    skippedCooldown: number
    skippedNoBaseline: number
    enqueuedAt: string
  }>> {
    const response = await api.post(`/monitoring/recheck-all/${domainId}`)
    return response.data
  }

  /**
   * Bulk-dismiss all "pending verification" URLs for a domain. Sets their
   * monitoring_status to 'none' so the awaiting-verification banner clears.
   * Used when the user doesn't intend to monitor those pages.
   */
  async dismissPendingMonitoring(domainId: string): Promise<ApiResponse<{
    dismissedCount: number
  }>> {
    const response = await api.post(`/monitoring/dismiss-pending/${domainId}`)
    return response.data
  }

  /**
   * Swap the monitored slot from one URL to another. Pauses `pauseUrlId`
   * (freeing a cap slot) and activates monitoring on `activateUrlId`.
   * Requires pauseUrlId to be currently verified and activateUrlId to be
   * deployment-detected.
   */
  async swapMonitoring(pauseUrlId: string, activateUrlId: string): Promise<ApiResponse<any>> {
    const response = await api.post('/monitoring/swap', { pauseUrlId, activateUrlId })
    return response.data
  }

  async getMonitoredUrls(): Promise<ApiResponse<Array<{ id: string; url: string; path: string }>>> {
    const response = await api.get('/monitoring/monitored')
    return response.data
  }
}

export const apiService = new ApiService()

// React Query hooks for common operations
export const useApiQuery = () => {
  const api = useApi()
  return api
}
